/**
 * Generated by orval v8.11.0 🍺
 * Do not edit manually.
 * PHC Threat Analytics API
 * FastAPI service that exposes the PHC Threat Analytics outputs.
 * OpenAPI spec version: 0.0.0
 */
import {
  useMutation,
  useQuery
} from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  MutationFunction,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseMutationOptions,
  UseMutationResult,
  UseQueryOptions,
  UseQueryResult
} from '@tanstack/react-query';

import { threatAnalyticsAxiosGenInstance } from './threatAnalyticsApiMutator';
/**
 * Shared fields between request and response payloads.
 */
export interface HawaiiCovid {
  reported_at: string;
  week_ended_at: string;
  county: string;
  new_cases?: number | null;
  new_positive_tests?: number | null;
  total_test_encounters?: number | null;
  weekly_cases?: number | null;
  weekly_test_positivity?: number | null;
  rolling_avg_cases_last_7_days?: number | null;
  rolling_avg_test_positivity_last_7_days?: number | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting Hawaii COVID metrics.
 */
export interface HawaiiCovidUpsertRequest {
  records: HawaiiCovid[];
}

export interface ValidationError {
  loc: (string | number)[];
  msg: string;
  type: string;
}

export interface HTTPValidationError {
  detail?: ValidationError[];
}

/**
 * Composite key required to identify a record.
 */
export interface HawaiiCovidKey {
  reported_at: string;
  county: string;
}

/**
 * Shared fields between request and response payloads.
 */
export interface Nssp {
  week_ended_at: string;
  geography: string;
  county: string;
  percent_visits_covid?: number | null;
  percent_visits_influenza?: number | null;
  percent_visits_rsv?: number | null;
  hsa: string;
  hsa_counties: string;
  hsa_nci_id: string;
  fips: string;
  trend_source: string;
  buildnumber: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting NSSP metrics.
 */
export interface NsspUpsertRequest {
  records: Nssp[];
}

/**
 * Composite key required to identify a record.
 */
export interface NsspKey {
  week_ended_at: string;
  geography: string;
  county: string;
}

/**
 * Pathogen identifier for a NWSS sample, derived from the CDC dataset name.
 */
export type Pathogen = typeof Pathogen[keyof typeof Pathogen];


export const Pathogen = {
  mpox: 'mpox',
  covid: 'covid',
  influenza_a: 'influenza_a',
  rsv: 'rsv',
  avian_influenza_a: 'avian_influenza_a',
  measles: 'measles',
} as const;

/**
 * Shared fields between NWSS request and response payloads.
 */
export interface Nwss {
  id?: string | null;
  pathogen: Pathogen;
  record_id: string;
  site: string;
  state_territory: string;
  source?: string | null;
  county_fips?: string | null;
  counties_served?: string | null;
  population_served?: number | null;
  sample_id?: string | null;
  sample_collect_date: string;
  sample_type?: string | null;
  sample_matrix?: string | null;
  sample_location?: string | null;
  flow_rate?: number | null;
  concentration_method?: string | null;
  pasteurized?: string | null;
  pcr_type?: string | null;
  extraction_method?: string | null;
  major_lab_method?: string | null;
  inhibition_detect?: string | null;
  inhibition_adjust?: string | null;
  ntc_amplify?: string | null;
  pcr_target?: string | null;
  pcr_gene_target_agg?: string | null;
  pcr_target_avg_conc?: number | null;
  pcr_target_units?: string | null;
  lod_sewage?: number | null;
  pcr_target_detect?: string | null;
  pcr_target_avg_conc_lin?: number | null;
  pcr_target_flowpop_lin?: number | null;
  pcr_target_mic_lin?: number | null;
  hum_frac_target_mic?: string | null;
  hum_frac_mic_conc?: number | null;
  hum_frac_mic_unit?: string | null;
  rec_eff_percent?: number | null;
  rec_eff_target_name?: string | null;
  rec_eff_spike_matrix?: string | null;
  rec_eff_spike_conc?: number | null;
  date_updated: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting NWSS metrics.
 */
export interface NwssUpsertRequest {
  records: Nwss[];
}

/**
 * Compact response for large bulk upserts.
 */
export interface NwssUpsertSummary {
  requested: number;
  stored: number;
  chunk_size: number;
  chunk_count: number;
}

/**
 * Composite business key required to identify a NWSS record.
 */
export interface NwssKey {
  pathogen: Pathogen;
  record_id: string;
}

/**
 * Latest raw CDC sample date known to TA.

Used by the FE header's freshness indicator. Named ``_at`` to match
the timestamp-column convention used elsewhere in this service; lives
on the raw ``/nwss`` prefix because it reads ``MAX(sample_collect_date)``
from the raw table, not from derived events.
 */
export interface LatestNwssSample {
  latest_sample_collected_at: string | null;
}

/**
 * Geographic aggregation level for a clustered anomaly event.
 */
export type EventGeoLevel = typeof EventGeoLevel[keyof typeof EventGeoLevel];


export const EventGeoLevel = {
  county: 'county',
  state: 'state',
} as const;

/**
 * Shared fields for anomaly event cluster request and response payloads.
 */
export interface NwssAnomalyEventCluster {
  id?: string | null;
  event_id: string;
  record_ids: string;
  pcr_target: string;
  pathogen: Pathogen;
  disease: string;
  event_geo_level: EventGeoLevel;
  geo_fips: string;
  geo_name: string;
  state_info: string;
  state_territory: string;
  counties_fips: string;
  counties_names: string;
  event_start_date: string;
  latest_ongoing_date?: string | null;
  event_end_date?: string | null;
  anomaly_count: number;
  source_count: number;
  description: string;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting anomaly event clusters.
 */
export interface NwssAnomalyEventClusterUpsertRequest {
  records: NwssAnomalyEventCluster[];
}

/**
 * Response for bulk upsert — count only.
 */
export interface NwssAnomalyEventClusterUpsertResponse {
  upserted: number;
}

/**
 * Business key required to identify an anomaly event cluster row.
 */
export interface NwssAnomalyEventClusterKey {
  event_id: string;
}

export interface BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet {
  pathogen?: Pathogen[] | null;
  disease?: string[] | null;
}

/**
 * Distinct filter option values for events visible in a date window.
 */
export interface NwssBiothreatSignalsFacets {
  diseases: string[];
  states: string[];
}

export type BiothreatSignalSource = typeof BiothreatSignalSource[keyof typeof BiothreatSignalSource];


export const BiothreatSignalSource = {
  nwss: 'nwss',
  gdelt: 'gdelt',
} as const;

export type GdeltMetaGeoLevel = typeof GdeltMetaGeoLevel[keyof typeof GdeltMetaGeoLevel];


export const GdeltMetaGeoLevel = {
  country: 'country',
  state: 'state',
} as const;

export interface GdeltSignalMember {
  id: string;
  title: string;
  url: string;
  source_name: string;
  publish_time: string;
}

export interface GdeltMeta {
  geo_level: GdeltMetaGeoLevel;
  first_reported_at: string;
  most_recent_members: GdeltSignalMember[];
  cluster_member_title_summary?: string | null;
}

export type NwssMetaAggregation = typeof NwssMetaAggregation[keyof typeof NwssMetaAggregation];


export const NwssMetaAggregation = {
  county: 'county',
  state: 'state',
} as const;

export type NwssMetaStatus = typeof NwssMetaStatus[keyof typeof NwssMetaStatus];


export const NwssMetaStatus = {
  start: 'start',
  ongoing: 'ongoing',
  ended: 'ended',
} as const;

export interface NwssMeta {
  event_id: string;
  aggregation: NwssMetaAggregation;
  pathogen: Pathogen;
  disease: string;
  county_fips: string | null;
  state_fips: string | null;
  state_territory: string | null;
  start_date: string;
  end_date: string;
  last_detection_date: string;
  window_days: number;
  status: NwssMetaStatus;
  is_ended_confirmed: boolean;
  n_detections: number;
  n_sites: number;
  site_ids: string[];
  member_record_ids: string[];
  counties?: string[];
}

/**
 * Unified row shape served to location-fe's Biothreat Signals table.

Populated by ``/nwss/events`` (NWSS) today. Forward-compatible with a
future GDELT shaper: the ``source`` discriminator and per-source
``*_meta`` nullable blocks leave room to add GDELT without breaking the
wire format, but no GDELT producer lives in this service yet (GDELT is
currently served by phoenix).
 */
export interface BiothreatSignal {
  id: string;
  source: BiothreatSignalSource;
  cluster_id: string;
  is_centroid: boolean;
  is_archived: boolean;
  cluster_count: number;
  modern_score: number | null;
  legacy_score: number | null;
  title: string;
  description: string | null;
  url: string;
  publish_time: string;
  source_name: string;
  pruned_themes: string[];
  clean_disease_codes: string[];
  resolved_location: string;
  threat_category: string | null;
  impact_score?: number | null;
  gdelt_meta?: GdeltMeta | null;
  nwss_meta?: NwssMeta | null;
}

/**
 * Envelope matching phoenix /api/v1/ai/candidates/ so the FE can reuse
``useAlertCandidates`` shape-compatibly.
 */
export interface BiothreatSignalList {
  count: number;
  results: BiothreatSignal[];
  last_updated?: string | null;
}

/**
 * A raw NWSS sample annotated with its role in a precomputed event.
 */
export interface NwssSignalSample {
  id?: string | null;
  pathogen: Pathogen;
  record_id: string;
  site: string;
  state_territory: string;
  source?: string | null;
  county_fips?: string | null;
  counties_served?: string | null;
  population_served?: number | null;
  sample_id?: string | null;
  sample_collect_date: string;
  sample_type?: string | null;
  sample_matrix?: string | null;
  sample_location?: string | null;
  flow_rate?: number | null;
  concentration_method?: string | null;
  pasteurized?: string | null;
  pcr_type?: string | null;
  extraction_method?: string | null;
  major_lab_method?: string | null;
  inhibition_detect?: string | null;
  inhibition_adjust?: string | null;
  ntc_amplify?: string | null;
  pcr_target?: string | null;
  pcr_gene_target_agg?: string | null;
  pcr_target_avg_conc?: number | null;
  pcr_target_units?: string | null;
  lod_sewage?: number | null;
  pcr_target_detect?: string | null;
  pcr_target_avg_conc_lin?: number | null;
  pcr_target_flowpop_lin?: number | null;
  pcr_target_mic_lin?: number | null;
  hum_frac_target_mic?: string | null;
  hum_frac_mic_conc?: number | null;
  hum_frac_mic_unit?: string | null;
  rec_eff_percent?: number | null;
  rec_eff_target_name?: string | null;
  rec_eff_spike_matrix?: string | null;
  rec_eff_spike_conc?: number | null;
  date_updated: string;
  created_at?: string | null;
  updated_at?: string | null;
  is_event_member: boolean;
  is_detected: boolean;
}

/**
 * Full sampling history underlying one precomputed NWSS signal.
 */
export interface NwssSignalSampleList {
  count: number;
  member_count: number;
  site_count: number;
  window_start: string;
  window_end: string;
  truncated: boolean;
  results: NwssSignalSample[];
}

/**
 * Shared fields between binary-detection request and response payloads.
 */
export interface NwssBinaryDetection {
  id?: string | null;
  pathogen: Pathogen;
  record_id: string;
  is_detected: boolean;
  algorithm_version: string;
  detected_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting binary-detection rows.
 */
export interface NwssBinaryDetectionUpsertRequest {
  records: NwssBinaryDetection[];
}

/**
 * Composite business key required to identify a binary-detection row.
 */
export interface NwssBinaryDetectionKey {
  pathogen: Pathogen;
  record_id: string;
}

export type BiothreatSignalDetailDetectionsItem = { [key: string]: unknown };

export interface BiothreatSignalDetail {
  signal: BiothreatSignal;
  detections: BiothreatSignalDetailDetectionsItem[];
}

/**
 * Shared fields between create and response payloads.
 */
export interface WastewaterPCRAnalysis {
  phc_site_id: string;
  sampling_end_date: string;
  target_name: string;
  target_result: string;
  target_copies_per_l?: number | null;
  fecal_indicator_copies_per_l?: number | null;
  fecal_normalized_target_copies_per_l?: number | null;
  fecal_normalized_target_copies_per_l_lod?: number | null;
  log_fecal_normalized_target_lod?: number | null;
  target_percentile?: number | null;
  rolling_median?: number | null;
  residual?: number | null;
  residual_threshold?: number | null;
  residual_flag?: string | null;
  delta?: number | null;
  delta_flag?: string | null;
  spike_flag?: string | null;
  growth_flag?: string | null;
  baseline_p10?: number | null;
  slope?: number | null;
  surge_flag?: string | null;
  rare_flag?: string | null;
  anomaly_flag?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting wastewater PCR analysis items.
 */
export interface WastewaterPCRAnalysisUpsertRequest {
  records: WastewaterPCRAnalysis[];
}

/**
 * Composite key required to identify a measurement.
 */
export interface WastewaterPCRAnalysisKey {
  phc_site_id: string;
  sampling_end_date: string;
  target_name: string;
}

/**
 * Shared fields between create and response payloads for activity level.
 */
export interface WastewaterPCRActivityLevel {
  id?: string | null;
  phc_site_id: string;
  sampling_end_date: string;
  target_name: string;
  target_result: string;
  target_copies_per_l?: number | null;
  fecal_indicator_copies_per_l?: number | null;
  fecal_normalized_target_copies_per_l?: number | null;
  fecal_normalized_target_copies_per_l_lod?: number | null;
  season_status?: string | null;
  virus_type?: string | null;
  trend?: string | null;
  level?: string | null;
  wastewater_category?: string | null;
  wastewater_category_v2?: string | null;
  trend_v2?: string | null;
  log_fecal_normalized_target_lod_v2?: number | null;
  p10_v2?: number | null;
  std_v2?: number | null;
  distance_v2?: number | null;
  reestimation_met_v2?: boolean | null;
  ideal_window_start_v2?: string | null;
  ideal_window_end_v2?: string | null;
  prev_ideal_start_v2?: string | null;
  prev_ideal_end_v2?: string | null;
  sample_count_v2?: number | null;
  sample_count_lookback_v2?: number | null;
  current_baseline_start_v2?: string | null;
  current_baseline_end_v2?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/**
 * Request body for bulk upserting wastewater PCR activity level items.
 */
export interface WastewaterPCRActivityLevelUpsertRequest {
  records: WastewaterPCRActivityLevel[];
}

/**
 * Composite key required to identify an activity level measurement.
 */
export interface WastewaterPCRActivityLevelKey {
  phc_site_id: string;
  sampling_end_date: string;
  target_name: string;
}

export interface BiomeshContactCreate {
  contact_id?: string | null;
  first_name: string;
  last_name: string;
  rank?: string | null;
  phone?: string | null;
  email?: string | null;
  unit_ids?: string[] | null;
  operation_ids?: string[] | null;
  sensor_ids?: string[] | null;
}

export type UnitType = typeof UnitType[keyof typeof UnitType];


export const UnitType = {
  UNIT_TYPE_UNSPECIFIED: 'UNIT_TYPE_UNSPECIFIED',
  UNIT_TYPE_MILITARY: 'UNIT_TYPE_MILITARY',
  UNIT_TYPE_CIVILIAN: 'UNIT_TYPE_CIVILIAN',
  UNIT_TYPE_NGO: 'UNIT_TYPE_NGO',
} as const;

export type BranchType = typeof BranchType[keyof typeof BranchType];


export const BranchType = {
  BRANCH_TYPE_UNSPECIFIED: 'BRANCH_TYPE_UNSPECIFIED',
  BRANCH_TYPE_ARMY: 'BRANCH_TYPE_ARMY',
  BRANCH_TYPE_NAVY: 'BRANCH_TYPE_NAVY',
  BRANCH_TYPE_MARINES: 'BRANCH_TYPE_MARINES',
  BRANCH_TYPE_AIR_FORCE: 'BRANCH_TYPE_AIR_FORCE',
  BRANCH_TYPE_SPACE_FORCE: 'BRANCH_TYPE_SPACE_FORCE',
} as const;

export interface BiomeshUnitRef {
  unit_id?: string | null;
  name: string;
  unit_type: UnitType;
  branch_type: BranchType;
  uic: string;
}

export interface BiomeshOperationRef {
  operation_id?: string | null;
  name: string;
}

export type OperationalStatus = typeof OperationalStatus[keyof typeof OperationalStatus];


export const OperationalStatus = {
  OPERATIONAL_STATUS_UNSPECIFIED: 'OPERATIONAL_STATUS_UNSPECIFIED',
  OPERATIONAL_STATUS_FMC: 'OPERATIONAL_STATUS_FMC',
  OPERATIONAL_STATUS_PMC: 'OPERATIONAL_STATUS_PMC',
  OPERATIONAL_STATUS_NMC: 'OPERATIONAL_STATUS_NMC',
} as const;

export type SensorType = typeof SensorType[keyof typeof SensorType];


export const SensorType = {
  SENSOR_TYPE_UNSPECIFIED: 'SENSOR_TYPE_UNSPECIFIED',
  SENSOR_TYPE_WEATHER_STATION: 'SENSOR_TYPE_WEATHER_STATION',
  SENSOR_TYPE_CAMERA: 'SENSOR_TYPE_CAMERA',
  SENSOR_TYPE_AIR_COLLECTION: 'SENSOR_TYPE_AIR_COLLECTION',
  SENSOR_TYPE_WATER_COLLECTION: 'SENSOR_TYPE_WATER_COLLECTION',
  SENSOR_TYPE_SURFACE_COLLECTION: 'SENSOR_TYPE_SURFACE_COLLECTION',
  SENSOR_TYPE_LABORATORY: 'SENSOR_TYPE_LABORATORY',
  SENSOR_TYPE_WEARABLE: 'SENSOR_TYPE_WEARABLE',
  SENSOR_TYPE_UNMANNED_SYSTEMS: 'SENSOR_TYPE_UNMANNED_SYSTEMS',
} as const;

export type SensorFunction = typeof SensorFunction[keyof typeof SensorFunction];


export const SensorFunction = {
  SENSOR_FUNCTION_UNSPECIFIED: 'SENSOR_FUNCTION_UNSPECIFIED',
  SENSOR_FUNCTION_COLLECTOR_DETECTOR: 'SENSOR_FUNCTION_COLLECTOR_DETECTOR',
  SENSOR_FUNCTION_DIAGNOSTIC_TOOL: 'SENSOR_FUNCTION_DIAGNOSTIC_TOOL',
  SENSOR_FUNCTION_VECTOR_DIAGNOSTIC_TOOL: 'SENSOR_FUNCTION_VECTOR_DIAGNOSTIC_TOOL',
  SENSOR_FUNCTION_CLASSIFIER_SURVEILLANCE: 'SENSOR_FUNCTION_CLASSIFIER_SURVEILLANCE',
} as const;

export interface BiomeshSensorRef {
  sensor_id?: string | null;
  sensor_name: string;
  operational_status: OperationalStatus;
  operational_status_updated_at?: string | null;
  sensor_type: SensorType;
  sensor_function?: SensorFunction | null;
  sensor_make_model?: string | null;
  sensor_notes?: string | null;
  last_sample_collected_at?: string | null;
  last_sample_result_reported_at?: string | null;
  platform_id?: string | null;
}

export interface BiomeshContactResponse {
  created_at?: string | null;
  updated_at?: string | null;
  org_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  contact_id: string;
  first_name: string;
  last_name: string;
  rank?: string | null;
  phone?: string | null;
  email?: string | null;
  units?: BiomeshUnitRef[] | null;
  operations?: BiomeshOperationRef[] | null;
  sensors?: BiomeshSensorRef[] | null;
}

export interface BiomeshContactUpsertRecord {
  contact_id: string;
  first_name: string;
  last_name: string;
  rank?: string | null;
  phone?: string | null;
  email?: string | null;
  unit_ids?: string[] | null;
  operation_ids?: string[] | null;
  sensor_ids?: string[] | null;
}

export interface BiomeshContactUpsertRequest {
  records: BiomeshContactUpsertRecord[];
}

export interface BiomeshContactUpdate {
  contact_id?: string | null;
  first_name: string;
  last_name: string;
  rank?: string | null;
  phone?: string | null;
  email?: string | null;
  unit_ids?: string[] | null;
  operation_ids?: string[] | null;
  sensor_ids?: string[] | null;
}

export interface BiomeshContactPatch {
  first_name?: string | null;
  last_name?: string | null;
  rank?: string | null;
  phone?: string | null;
  email?: string | null;
}

export type EventStatus = typeof EventStatus[keyof typeof EventStatus];


export const EventStatus = {
  EVENT_STATUS_INITIAL: 'EVENT_STATUS_INITIAL',
  EVENT_STATUS_CONFIRMED: 'EVENT_STATUS_CONFIRMED',
  EVENT_STATUS_ARCHIVED: 'EVENT_STATUS_ARCHIVED',
} as const;

export interface BiomeshEventCreate {
  event_id?: string | null;
  sensor_id: string;
  name: string;
  observations?: string | null;
  status?: EventStatus | null;
  resources?: string | null;
}

export interface BiomeshEventResponse {
  created_at?: string | null;
  updated_at?: string | null;
  org_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  event_id: string;
  sensor_id: string;
  name: string;
  observations?: string | null;
  status?: EventStatus | null;
  resources?: string | null;
  sensor?: BiomeshSensorRef | null;
}

export interface BiomeshEventUpsertRecord {
  event_id: string;
  sensor_id: string;
  name: string;
  observations?: string | null;
  status?: EventStatus | null;
  resources?: string | null;
}

export interface BiomeshEventUpsertRequest {
  records: BiomeshEventUpsertRecord[];
}

export interface BiomeshEventUpdate {
  event_id?: string | null;
  sensor_id: string;
  name: string;
  observations?: string | null;
  status?: EventStatus | null;
  resources?: string | null;
}

export interface BiomeshEventPatch {
  sensor_id?: string | null;
  name?: string | null;
  observations?: string | null;
  status?: EventStatus | null;
  resources?: string | null;
}

export interface BiomeshOperationCreate {
  operation_id?: string | null;
  name: string;
  unit_ids?: string[] | null;
  platform_ids?: string[] | null;
  contact_ids?: string[] | null;
}

export type PlatformType = typeof PlatformType[keyof typeof PlatformType];


export const PlatformType = {
  PLATFORM_TYPE_UNSPECIFIED: 'PLATFORM_TYPE_UNSPECIFIED',
  PLATFORM_TYPE_FACILITY: 'PLATFORM_TYPE_FACILITY',
  PLATFORM_TYPE_EXPEDITIONARY: 'PLATFORM_TYPE_EXPEDITIONARY',
  PLATFORM_TYPE_VESSEL: 'PLATFORM_TYPE_VESSEL',
  PLATFORM_TYPE_AIRCRAFT: 'PLATFORM_TYPE_AIRCRAFT',
  PLATFORM_TYPE_UNMANNED_SYSTEM: 'PLATFORM_TYPE_UNMANNED_SYSTEM',
} as const;

export interface BiomeshPlatformRef {
  platform_id?: string | null;
  name: string;
  platform_type: PlatformType;
  centroid?: string | null;
}

export interface BiomeshContactRef {
  contact_id?: string | null;
  first_name: string;
  last_name: string;
  rank?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface BiomeshOperationResponse {
  created_at?: string | null;
  updated_at?: string | null;
  org_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  operation_id: string;
  name: string;
  units?: BiomeshUnitRef[] | null;
  platforms?: BiomeshPlatformRef[] | null;
  contacts?: BiomeshContactRef[] | null;
}

export interface BiomeshOperationUpsertRecord {
  operation_id: string;
  name: string;
  unit_ids?: string[] | null;
  platform_ids?: string[] | null;
  contact_ids?: string[] | null;
}

export interface BiomeshOperationUpsertRequest {
  records: BiomeshOperationUpsertRecord[];
}

export interface BiomeshOperationUpdate {
  operation_id?: string | null;
  name: string;
  unit_ids?: string[] | null;
  platform_ids?: string[] | null;
  contact_ids?: string[] | null;
}

export interface BiomeshOperationPatch {
  name?: string | null;
}

export interface BiomeshPlatformCreate {
  platform_id?: string | null;
  name: string;
  platform_type: PlatformType;
  centroid?: string | null;
  operation_ids?: string[] | null;
}

export interface BiomeshPlatformResponse {
  created_at?: string | null;
  updated_at?: string | null;
  org_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  platform_id: string;
  name: string;
  platform_type: PlatformType;
  centroid?: string | null;
  operations?: BiomeshOperationRef[] | null;
}

export interface BiomeshPlatformUpsertRecord {
  platform_id: string;
  name: string;
  platform_type: PlatformType;
  centroid?: string | null;
  operation_ids?: string[] | null;
}

export interface BiomeshPlatformUpsertRequest {
  records: BiomeshPlatformUpsertRecord[];
}

export interface BiomeshPlatformUpdate {
  platform_id?: string | null;
  name: string;
  platform_type: PlatformType;
  centroid?: string | null;
  operation_ids?: string[] | null;
}

export interface BiomeshPlatformPatch {
  name?: string | null;
  platform_type?: PlatformType | null;
  centroid?: string | null;
}

export interface BiomeshSensorCreate {
  sensor_id?: string | null;
  sensor_name: string;
  operational_status: OperationalStatus;
  operational_status_updated_at?: string | null;
  sensor_type: SensorType;
  sensor_function?: SensorFunction | null;
  sensor_make_model?: string | null;
  sensor_notes?: string | null;
  last_sample_collected_at?: string | null;
  last_sample_result_reported_at?: string | null;
  platform_id?: string | null;
  contact_ids?: string[] | null;
}

export interface BiomeshSensorResponse {
  created_at?: string | null;
  updated_at?: string | null;
  org_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  sensor_id: string;
  sensor_name: string;
  operational_status: OperationalStatus;
  operational_status_updated_at?: string | null;
  sensor_type: SensorType;
  sensor_function?: SensorFunction | null;
  sensor_make_model?: string | null;
  sensor_notes?: string | null;
  last_sample_collected_at?: string | null;
  last_sample_result_reported_at?: string | null;
  platform_id?: string | null;
  contacts?: BiomeshContactRef[] | null;
}

export interface BiomeshSensorUpsertRecord {
  sensor_id: string;
  sensor_name: string;
  operational_status: OperationalStatus;
  operational_status_updated_at?: string | null;
  sensor_type: SensorType;
  sensor_function?: SensorFunction | null;
  sensor_make_model?: string | null;
  sensor_notes?: string | null;
  last_sample_collected_at?: string | null;
  last_sample_result_reported_at?: string | null;
  platform_id?: string | null;
  contact_ids?: string[] | null;
}

export interface BiomeshSensorUpsertRequest {
  records: BiomeshSensorUpsertRecord[];
}

export interface BiomeshSensorUpdate {
  sensor_id?: string | null;
  sensor_name: string;
  operational_status: OperationalStatus;
  operational_status_updated_at?: string | null;
  sensor_type: SensorType;
  sensor_function?: SensorFunction | null;
  sensor_make_model?: string | null;
  sensor_notes?: string | null;
  last_sample_collected_at?: string | null;
  last_sample_result_reported_at?: string | null;
  platform_id?: string | null;
  contact_ids?: string[] | null;
}

export interface BiomeshSensorPatch {
  sensor_name?: string | null;
  operational_status?: OperationalStatus | null;
  operational_status_updated_at?: string | null;
  sensor_type?: SensorType | null;
  sensor_function?: SensorFunction | null;
  sensor_make_model?: string | null;
  sensor_notes?: string | null;
  last_sample_collected_at?: string | null;
  last_sample_result_reported_at?: string | null;
  platform_id?: string | null;
}

export interface BiomeshUnitCreate {
  unit_id?: string | null;
  name: string;
  unit_type: UnitType;
  branch_type: BranchType;
  uic: string;
  operation_ids?: string[] | null;
  contact_ids?: string[] | null;
}

export interface BiomeshUnitResponse {
  created_at?: string | null;
  updated_at?: string | null;
  org_id: string;
  created_by?: string | null;
  updated_by?: string | null;
  unit_id: string;
  name: string;
  unit_type: UnitType;
  branch_type: BranchType;
  uic: string;
  operations?: BiomeshOperationRef[] | null;
  contacts?: BiomeshContactRef[] | null;
}

export interface BiomeshUnitUpsertRecord {
  unit_id: string;
  name: string;
  unit_type: UnitType;
  branch_type: BranchType;
  uic: string;
  operation_ids?: string[] | null;
  contact_ids?: string[] | null;
}

export interface BiomeshUnitUpsertRequest {
  records: BiomeshUnitUpsertRecord[];
}

export interface BiomeshUnitUpdate {
  unit_id?: string | null;
  name: string;
  unit_type: UnitType;
  branch_type: BranchType;
  uic: string;
  operation_ids?: string[] | null;
  contact_ids?: string[] | null;
}

export interface BiomeshUnitPatch {
  name?: string | null;
  unit_type?: UnitType | null;
  branch_type?: BranchType | null;
  uic?: string | null;
}

/**
 * Request/response schema for GDELT articles.
 */
export interface GdeltArticle {
  raw_gkg_id: string;
  title?: string | null;
  url?: string | null;
  publish_time?: string | null;
  source_name?: string | null;
  raw_locations?: string | null;
  country_location_fips?: string | null;
  admin1_location_gkg?: string | null;
  country_location_iso?: string | null;
  state_location_iso?: string | null;
  location_display?: string | null;
  clean_disease_codes?: string[];
  embedding?: number[] | null;
  embedding_model?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface GdeltArticleUpsertRequest {
  records: GdeltArticle[];
}

export interface GdeltArticleUpsertResponse {
  upserted: number;
}

export interface GdeltListResponseGdeltArticle {
  count: number;
  results: GdeltArticle[];
}

/**
 * Geographic aggregation level for a GDELT cluster.
 */
export type GdeltGeoLevel = typeof GdeltGeoLevel[keyof typeof GdeltGeoLevel];


export const GdeltGeoLevel = {
  state: 'state',
  country: 'country',
} as const;

export type GdeltSignalClusterMostRecentMembersItem = { [key: string]: unknown };

export interface GdeltSignalCluster {
  cluster_id?: string | null;
  geo_level: GdeltGeoLevel;
  location_key_iso: string;
  resolved_location?: string | null;
  cluster_window_start: string;
  most_recent_member_published_at: string;
  cluster_count?: number;
  mean_member_embedding?: number[] | null;
  description?: string | null;
  url?: string | null;
  source_name?: string | null;
  cluster_member_title_summary?: string | null;
  impact_score?: number | null;
  disease_code?: string | null;
  threat_category?: string | null;
  most_recent_members?: GdeltSignalClusterMostRecentMembersItem[];
  created_at?: string | null;
  updated_at?: string | null;
}

export interface GdeltSignalClusterUpsertRequest {
  records: GdeltSignalCluster[];
}

export interface GdeltSignalClusterUpsertResponse {
  upserted: number;
  clusters: GdeltSignalCluster[];
}

export interface GdeltTruncateClustersResponse {
  members_deleted: number;
  clusters_deleted: number;
}

export interface GdeltListResponseGdeltSignalCluster {
  count: number;
  results: GdeltSignalCluster[];
}

export interface GdeltClusterMemberArticle {
  raw_gkg_id: string;
  title?: string | null;
  url?: string | null;
  source_name?: string | null;
  publish_time?: string | null;
}

export interface GdeltClusterMemberArticleList {
  count: number;
  total_count: number;
  results: GdeltClusterMemberArticle[];
}

/**
 * Partial cluster update for scoring, summarization, or display refresh.
 */
export interface GdeltSignalClusterPatch {
  impact_score?: 15 | 40 | 65 | 90 | null;
  resolved_location?: string | null;
  location_key_iso?: string | null;
  cluster_member_title_summary?: string | null;
  description?: string | null;
  url?: string | null;
  source_name?: string | null;
}

export interface ClusterSearchRequest {
  embedding: number[];
  geo_level: string;
  location_key_iso: string;
  disease_code: string;
  cosine_threshold?: number;
  lookback_window_days?: number;
}

export interface GdeltSignalClusterMember {
  cluster_id: string;
  raw_gkg_id: string;
}

export interface GdeltSignalClusterMemberUpsertRequest {
  records: GdeltSignalClusterMember[];
}

export interface GdeltSignalClusterMemberUpsertResponse {
  upserted: number;
}

export interface GdeltTruncateMembersResponse {
  deleted: number;
}

/**
 * Distinct filter option values for clusters visible in a date window.
 */
export interface GdeltBiothreatSignalsFacets {
  diseases: string[];
  countries: string[];
  states: string[];
}

export type ListHawaiiCovidHawaiiCovidListGetParams = {
county?: string | null;
reported_at_start?: string | null;
reported_at_end?: string | null;
week_ended_at_start?: string | null;
week_ended_at_end?: string | null;
limit?: number;
offset?: number;
};

export type ListNsspNsspListGetParams = {
geography?: string | null;
county?: string | null;
hsa?: string | null;
hsa_nci_id?: string | null;
fips?: string | null;
trend_source?: string | null;
buildnumber?: string | null;
week_ended_at_start?: string | null;
week_ended_at_end?: string | null;
limit?: number;
offset?: number;
};

export type UpsertNwssNwssUpsertPostParams = {
response_mode?: UpsertNwssNwssUpsertPostResponseMode;
};

export type UpsertNwssNwssUpsertPostResponseMode = typeof UpsertNwssNwssUpsertPostResponseMode[keyof typeof UpsertNwssNwssUpsertPostResponseMode];


export const UpsertNwssNwssUpsertPostResponseMode = {
  auto: 'auto',
  full: 'full',
  summary: 'summary',
} as const;

export type ListNwssNwssListGetParams = {
pathogen?: Pathogen | null;
state_territory?: string | null;
site?: string | null;
pcr_target?: string | null;
source?: string | null;
county_fips?: string | null;
sample_collect_date_start?: string | null;
sample_collect_date_end?: string | null;
limit?: number;
offset?: number;
};

export type ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams = {
pathogen?: Pathogen[] | null;
disease?: string[] | null;
event_geo_level?: EventGeoLevel | null;
geo_fips?: string | null;
state_territory?: string | null;
pcr_target?: string | null;
is_active?: boolean | null;
event_start_date_start?: string | null;
event_start_date_end?: string | null;
event_end_date_start?: string | null;
event_end_date_end?: string | null;
limit?: number;
offset?: number;
sort_by?: string[] | null;
sort_direction?: string[] | null;
};

export type ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams = {
start_date?: string | null;
end_date?: string | null;
include_ended?: boolean;
};

export type ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams = {
disease?: string[] | null;
state?: string | null;
start_date?: string | null;
end_date?: string | null;
event_geo_level?: EventGeoLevel | null;
include_ended?: boolean;
limit?: number;
offset?: number;
sort_by?: string[] | null;
sort_direction?: string[] | null;
};

export type ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams = {
/**
 * @minimum 0
 */
context_days?: number;
include_non_detects?: boolean;
};

export type ListNwssBinaryDetectionNwssBinaryDetectionListGetParams = {
pathogen?: Pathogen | null;
record_id?: string | null;
is_detected?: boolean | null;
algorithm_version?: string | null;
detected_at_start?: string | null;
detected_at_end?: string | null;
limit?: number;
offset?: number;
};

export type ListNwssEventsNwssEventsGetParams = {
pathogen?: Pathogen[] | null;
disease?: string[] | null;
state?: string | null;
county_fips?: string | null;
counties_served?: string | null;
aggregation?: ListNwssEventsNwssEventsGetAggregation;
start_date?: string | null;
end_date?: string | null;
include_ended?: boolean;
limit?: number;
offset?: number;
sort_by?: string[] | null;
sort_direction?: string[] | null;
};

export type ListNwssEventsNwssEventsGetAggregation = typeof ListNwssEventsNwssEventsGetAggregation[keyof typeof ListNwssEventsNwssEventsGetAggregation];


export const ListNwssEventsNwssEventsGetAggregation = {
  county: 'county',
  state: 'state',
  both: 'both',
} as const;

export type GetNwssEventDetailNwssEventsEventIdGetParams = {
start_date?: string | null;
};

export type ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams = {
phc_site_id?: string | null;
target_name?: string | null;
anomaly_flag?: string | null;
date_range_start?: string | null;
date_range_end?: string | null;
limit?: number;
offset?: number;
};

export type ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams = {
phc_site_id?: string | null;
target_name?: string | null;
level?: string | null;
virus_type?: string | null;
wastewater_category?: string | null;
trend?: string | null;
date_range_start?: string | null;
date_range_end?: string | null;
limit?: number;
offset?: number;
};

export type ListContactsBiomeshContactListGetParams = {
/**
 * Comma-separated relationship names to include in the response (e.g. 'units,operations'). Omit to exclude all relationships.
 */
include?: string | null;
search?: string | null;
limit?: number;
offset?: number;
};

export type ListEventsBiomeshEventListGetParams = {
search?: string | null;
sensor_id?: string | null;
limit?: number;
offset?: number;
};

export type ListOperationsBiomeshOperationListGetParams = {
/**
 * Comma-separated relationship names to include in the response (e.g. 'units,operations'). Omit to exclude all relationships.
 */
include?: string | null;
search?: string | null;
limit?: number;
offset?: number;
};

export type ListPlatformsBiomeshPlatformListGetParams = {
/**
 * Comma-separated relationship names to include in the response (e.g. 'units,operations'). Omit to exclude all relationships.
 */
include?: string | null;
search?: string | null;
limit?: number;
offset?: number;
};

export type ListSensorsBiomeshSensorListGetParams = {
/**
 * Comma-separated relationship names to include in the response (e.g. 'units,operations'). Omit to exclude all relationships.
 */
include?: string | null;
search?: string | null;
limit?: number;
offset?: number;
};

export type ListUnitsBiomeshUnitListGetParams = {
/**
 * Comma-separated relationship names to include in the response (e.g. 'units,operations'). Omit to exclude all relationships.
 */
include?: string | null;
search?: string | null;
limit?: number;
offset?: number;
};

export type ListGdeltArticlesGdeltArticlesListGetParams = {
unclustered?: boolean;
publish_time_start?: string | null;
publish_time_end?: string | null;
include_embeddings?: boolean;
limit?: number;
offset?: number;
};

export type UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams = {
include_embeddings?: boolean;
};

export type TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams = {
cluster_window_start_since?: string | null;
};

export type ListGdeltSignalClustersGdeltSignalClustersListGetParams = {
include_embeddings?: boolean;
limit?: number;
offset?: number;
};

export type ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams = {
dedup?: boolean;
};

export type GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams = {
include_embeddings?: boolean;
};

export type PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams = {
include_embeddings?: boolean;
};

export type SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams = {
include_embeddings?: boolean;
};

export type TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams = {
article_published_since?: string | null;
};

export type ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams = {
most_recent_member_published_at_start?: string | null;
most_recent_member_published_at_end?: string | null;
active_date_range_started_at?: string | null;
active_date_range_ended_at?: string | null;
has_impact_score?: boolean | null;
};

export type ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams = {
disease_code?: string | null;
geo_level?: GdeltGeoLevel | null;
resolved_location?: string | null;
most_recent_member_published_at_start?: string | null;
most_recent_member_published_at_end?: string | null;
active_date_range_started_at?: string | null;
active_date_range_ended_at?: string | null;
title_search?: string | null;
member_title_search?: string | null;
summary_search?: string | null;
impact_score_min?: number | null;
has_impact_score?: boolean | null;
limit?: number;
offset?: number;
sort_by?: string[] | null;
sort_direction?: string[] | null;
};

type AwaitedInput<T> = PromiseLike<T> | T;

      type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];



/**
 * Bulk upsert Hawaii COVID metrics.
 * @summary Create or update Hawaii COVID metrics
 */
export const upsertHawaiiCovidHawaiiCovidUpsertPost = (
    hawaiiCovidUpsertRequest: HawaiiCovidUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<HawaiiCovid[]>(
      {url: `/hawaii-covid/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: hawaiiCovidUpsertRequest, signal
    },
      options);
    }



export const getUpsertHawaiiCovidHawaiiCovidUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertHawaiiCovidHawaiiCovidUpsertPost>>, TError,{data: HawaiiCovidUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertHawaiiCovidHawaiiCovidUpsertPost>>, TError,{data: HawaiiCovidUpsertRequest}, TContext> => {

const mutationKey = ['upsertHawaiiCovidHawaiiCovidUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertHawaiiCovidHawaiiCovidUpsertPost>>, {data: HawaiiCovidUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertHawaiiCovidHawaiiCovidUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertHawaiiCovidHawaiiCovidUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertHawaiiCovidHawaiiCovidUpsertPost>>>
    export type UpsertHawaiiCovidHawaiiCovidUpsertPostMutationBody = HawaiiCovidUpsertRequest
    export type UpsertHawaiiCovidHawaiiCovidUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update Hawaii COVID metrics
 */
export const useUpsertHawaiiCovidHawaiiCovidUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertHawaiiCovidHawaiiCovidUpsertPost>>, TError,{data: HawaiiCovidUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertHawaiiCovidHawaiiCovidUpsertPost>>,
        TError,
        {data: HawaiiCovidUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertHawaiiCovidHawaiiCovidUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing Hawaii COVID entry.
 * @summary Update Hawaii COVID metrics
 */
export const updateHawaiiCovidHawaiiCovidPut = (
    hawaiiCovid: HawaiiCovid,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<HawaiiCovid>(
      {url: `/hawaii-covid`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: hawaiiCovid, signal
    },
      options);
    }



export const getUpdateHawaiiCovidHawaiiCovidPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateHawaiiCovidHawaiiCovidPut>>, TError,{data: HawaiiCovid}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateHawaiiCovidHawaiiCovidPut>>, TError,{data: HawaiiCovid}, TContext> => {

const mutationKey = ['updateHawaiiCovidHawaiiCovidPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateHawaiiCovidHawaiiCovidPut>>, {data: HawaiiCovid}> = (props) => {
          const {data} = props ?? {};

          return  updateHawaiiCovidHawaiiCovidPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateHawaiiCovidHawaiiCovidPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateHawaiiCovidHawaiiCovidPut>>>
    export type UpdateHawaiiCovidHawaiiCovidPutMutationBody = HawaiiCovid
    export type UpdateHawaiiCovidHawaiiCovidPutMutationError = HTTPValidationError

    /**
 * @summary Update Hawaii COVID metrics
 */
export const useUpdateHawaiiCovidHawaiiCovidPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateHawaiiCovidHawaiiCovidPut>>, TError,{data: HawaiiCovid}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateHawaiiCovidHawaiiCovidPut>>,
        TError,
        {data: HawaiiCovid},
        TContext
      > => {
      return useMutation(getUpdateHawaiiCovidHawaiiCovidPutMutationOptions(options), queryClient);
    }

/**
 * Delete an existing Hawaii COVID entry.
 * @summary Delete Hawaii COVID metrics
 */
export const deleteHawaiiCovidHawaiiCovidDelete = (
    hawaiiCovidKey: HawaiiCovidKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/hawaii-covid`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: hawaiiCovidKey, signal
    },
      options);
    }



export const getDeleteHawaiiCovidHawaiiCovidDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteHawaiiCovidHawaiiCovidDelete>>, TError,{data: HawaiiCovidKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteHawaiiCovidHawaiiCovidDelete>>, TError,{data: HawaiiCovidKey}, TContext> => {

const mutationKey = ['deleteHawaiiCovidHawaiiCovidDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteHawaiiCovidHawaiiCovidDelete>>, {data: HawaiiCovidKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteHawaiiCovidHawaiiCovidDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteHawaiiCovidHawaiiCovidDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteHawaiiCovidHawaiiCovidDelete>>>
    export type DeleteHawaiiCovidHawaiiCovidDeleteMutationBody = HawaiiCovidKey
    export type DeleteHawaiiCovidHawaiiCovidDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete Hawaii COVID metrics
 */
export const useDeleteHawaiiCovidHawaiiCovidDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteHawaiiCovidHawaiiCovidDelete>>, TError,{data: HawaiiCovidKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteHawaiiCovidHawaiiCovidDelete>>,
        TError,
        {data: HawaiiCovidKey},
        TContext
      > => {
      return useMutation(getDeleteHawaiiCovidHawaiiCovidDeleteMutationOptions(options), queryClient);
    }

/**
 * Return Hawaii COVID metrics with optional filters.
 * @summary List Hawaii COVID metrics
 */
export const listHawaiiCovidHawaiiCovidListGet = (
    params?: ListHawaiiCovidHawaiiCovidListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<HawaiiCovid[]>(
      {url: `/hawaii-covid/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListHawaiiCovidHawaiiCovidListGetQueryKey = (params?: ListHawaiiCovidHawaiiCovidListGetParams,) => {
    return [
    `/hawaii-covid/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListHawaiiCovidHawaiiCovidListGetQueryOptions = <TData = Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError = HTTPValidationError>(params?: ListHawaiiCovidHawaiiCovidListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListHawaiiCovidHawaiiCovidListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>> = ({ signal }) => listHawaiiCovidHawaiiCovidListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListHawaiiCovidHawaiiCovidListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>>
export type ListHawaiiCovidHawaiiCovidListGetQueryError = HTTPValidationError


export function useListHawaiiCovidHawaiiCovidListGet<TData = Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListHawaiiCovidHawaiiCovidListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>,
          TError,
          Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListHawaiiCovidHawaiiCovidListGet<TData = Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError = HTTPValidationError>(
 params?: ListHawaiiCovidHawaiiCovidListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>,
          TError,
          Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListHawaiiCovidHawaiiCovidListGet<TData = Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError = HTTPValidationError>(
 params?: ListHawaiiCovidHawaiiCovidListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List Hawaii COVID metrics
 */

export function useListHawaiiCovidHawaiiCovidListGet<TData = Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError = HTTPValidationError>(
 params?: ListHawaiiCovidHawaiiCovidListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listHawaiiCovidHawaiiCovidListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListHawaiiCovidHawaiiCovidListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Bulk upsert NSSP metrics.
 * @summary Create or update NSSP metrics
 */
export const upsertNsspNsspUpsertPost = (
    nsspUpsertRequest: NsspUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<Nssp[]>(
      {url: `/nssp/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: nsspUpsertRequest, signal
    },
      options);
    }



export const getUpsertNsspNsspUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNsspNsspUpsertPost>>, TError,{data: NsspUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertNsspNsspUpsertPost>>, TError,{data: NsspUpsertRequest}, TContext> => {

const mutationKey = ['upsertNsspNsspUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertNsspNsspUpsertPost>>, {data: NsspUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertNsspNsspUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertNsspNsspUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertNsspNsspUpsertPost>>>
    export type UpsertNsspNsspUpsertPostMutationBody = NsspUpsertRequest
    export type UpsertNsspNsspUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update NSSP metrics
 */
export const useUpsertNsspNsspUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNsspNsspUpsertPost>>, TError,{data: NsspUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertNsspNsspUpsertPost>>,
        TError,
        {data: NsspUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertNsspNsspUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing NSSP entry.
 * @summary Update NSSP metrics
 */
export const updateNsspNsspPut = (
    nssp: Nssp,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<Nssp>(
      {url: `/nssp`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: nssp, signal
    },
      options);
    }



export const getUpdateNsspNsspPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNsspNsspPut>>, TError,{data: Nssp}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNsspNsspPut>>, TError,{data: Nssp}, TContext> => {

const mutationKey = ['updateNsspNsspPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNsspNsspPut>>, {data: Nssp}> = (props) => {
          const {data} = props ?? {};

          return  updateNsspNsspPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNsspNsspPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateNsspNsspPut>>>
    export type UpdateNsspNsspPutMutationBody = Nssp
    export type UpdateNsspNsspPutMutationError = HTTPValidationError

    /**
 * @summary Update NSSP metrics
 */
export const useUpdateNsspNsspPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNsspNsspPut>>, TError,{data: Nssp}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNsspNsspPut>>,
        TError,
        {data: Nssp},
        TContext
      > => {
      return useMutation(getUpdateNsspNsspPutMutationOptions(options), queryClient);
    }

/**
 * Delete an existing NSSP entry.
 * @summary Delete NSSP metrics
 */
export const deleteNsspNsspDelete = (
    nsspKey: NsspKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/nssp`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: nsspKey, signal
    },
      options);
    }



export const getDeleteNsspNsspDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNsspNsspDelete>>, TError,{data: NsspKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNsspNsspDelete>>, TError,{data: NsspKey}, TContext> => {

const mutationKey = ['deleteNsspNsspDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNsspNsspDelete>>, {data: NsspKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteNsspNsspDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNsspNsspDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNsspNsspDelete>>>
    export type DeleteNsspNsspDeleteMutationBody = NsspKey
    export type DeleteNsspNsspDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete NSSP metrics
 */
export const useDeleteNsspNsspDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNsspNsspDelete>>, TError,{data: NsspKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNsspNsspDelete>>,
        TError,
        {data: NsspKey},
        TContext
      > => {
      return useMutation(getDeleteNsspNsspDeleteMutationOptions(options), queryClient);
    }

/**
 * Return NSSP metrics with optional filters.
 * @summary List NSSP metrics
 */
export const listNsspNsspListGet = (
    params?: ListNsspNsspListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<Nssp[]>(
      {url: `/nssp/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNsspNsspListGetQueryKey = (params?: ListNsspNsspListGetParams,) => {
    return [
    `/nssp/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNsspNsspListGetQueryOptions = <TData = Awaited<ReturnType<typeof listNsspNsspListGet>>, TError = HTTPValidationError>(params?: ListNsspNsspListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNsspNsspListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNsspNsspListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNsspNsspListGet>>> = ({ signal }) => listNsspNsspListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNsspNsspListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNsspNsspListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNsspNsspListGet>>>
export type ListNsspNsspListGetQueryError = HTTPValidationError


export function useListNsspNsspListGet<TData = Awaited<ReturnType<typeof listNsspNsspListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListNsspNsspListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNsspNsspListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNsspNsspListGet>>,
          TError,
          Awaited<ReturnType<typeof listNsspNsspListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNsspNsspListGet<TData = Awaited<ReturnType<typeof listNsspNsspListGet>>, TError = HTTPValidationError>(
 params?: ListNsspNsspListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNsspNsspListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNsspNsspListGet>>,
          TError,
          Awaited<ReturnType<typeof listNsspNsspListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNsspNsspListGet<TData = Awaited<ReturnType<typeof listNsspNsspListGet>>, TError = HTTPValidationError>(
 params?: ListNsspNsspListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNsspNsspListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List NSSP metrics
 */

export function useListNsspNsspListGet<TData = Awaited<ReturnType<typeof listNsspNsspListGet>>, TError = HTTPValidationError>(
 params?: ListNsspNsspListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNsspNsspListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNsspNsspListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Bulk upsert NWSS metrics.
 * @summary Create or update NWSS metrics
 */
export const upsertNwssNwssUpsertPost = (
    nwssUpsertRequest: NwssUpsertRequest,
    params?: UpsertNwssNwssUpsertPostParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<Nwss[] | NwssUpsertSummary>(
      {url: `/nwss/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: nwssUpsertRequest,
        params, signal
    },
      options);
    }



export const getUpsertNwssNwssUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssNwssUpsertPost>>, TError,{data: NwssUpsertRequest;params?: UpsertNwssNwssUpsertPostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertNwssNwssUpsertPost>>, TError,{data: NwssUpsertRequest;params?: UpsertNwssNwssUpsertPostParams}, TContext> => {

const mutationKey = ['upsertNwssNwssUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertNwssNwssUpsertPost>>, {data: NwssUpsertRequest;params?: UpsertNwssNwssUpsertPostParams}> = (props) => {
          const {data,params} = props ?? {};

          return  upsertNwssNwssUpsertPost(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertNwssNwssUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertNwssNwssUpsertPost>>>
    export type UpsertNwssNwssUpsertPostMutationBody = NwssUpsertRequest
    export type UpsertNwssNwssUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update NWSS metrics
 */
export const useUpsertNwssNwssUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssNwssUpsertPost>>, TError,{data: NwssUpsertRequest;params?: UpsertNwssNwssUpsertPostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertNwssNwssUpsertPost>>,
        TError,
        {data: NwssUpsertRequest;params?: UpsertNwssNwssUpsertPostParams},
        TContext
      > => {
      return useMutation(getUpsertNwssNwssUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Bulk upsert NWSS metrics and return only aggregate counts.
 * @summary Create or update NWSS metrics (summary response)
 */
export const upsertNwssSummaryNwssUpsertSummaryPost = (
    nwssUpsertRequest: NwssUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssUpsertSummary>(
      {url: `/nwss/upsert/summary`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: nwssUpsertRequest, signal
    },
      options);
    }



export const getUpsertNwssSummaryNwssUpsertSummaryPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssSummaryNwssUpsertSummaryPost>>, TError,{data: NwssUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertNwssSummaryNwssUpsertSummaryPost>>, TError,{data: NwssUpsertRequest}, TContext> => {

const mutationKey = ['upsertNwssSummaryNwssUpsertSummaryPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertNwssSummaryNwssUpsertSummaryPost>>, {data: NwssUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertNwssSummaryNwssUpsertSummaryPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertNwssSummaryNwssUpsertSummaryPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertNwssSummaryNwssUpsertSummaryPost>>>
    export type UpsertNwssSummaryNwssUpsertSummaryPostMutationBody = NwssUpsertRequest
    export type UpsertNwssSummaryNwssUpsertSummaryPostMutationError = HTTPValidationError

    /**
 * @summary Create or update NWSS metrics (summary response)
 */
export const useUpsertNwssSummaryNwssUpsertSummaryPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssSummaryNwssUpsertSummaryPost>>, TError,{data: NwssUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertNwssSummaryNwssUpsertSummaryPost>>,
        TError,
        {data: NwssUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertNwssSummaryNwssUpsertSummaryPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing NWSS entry.
 * @summary Update NWSS metrics
 */
export const updateNwssNwssPut = (
    nwss: Nwss,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<Nwss>(
      {url: `/nwss`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: nwss, signal
    },
      options);
    }



export const getUpdateNwssNwssPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNwssNwssPut>>, TError,{data: Nwss}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNwssNwssPut>>, TError,{data: Nwss}, TContext> => {

const mutationKey = ['updateNwssNwssPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNwssNwssPut>>, {data: Nwss}> = (props) => {
          const {data} = props ?? {};

          return  updateNwssNwssPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNwssNwssPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateNwssNwssPut>>>
    export type UpdateNwssNwssPutMutationBody = Nwss
    export type UpdateNwssNwssPutMutationError = HTTPValidationError

    /**
 * @summary Update NWSS metrics
 */
export const useUpdateNwssNwssPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNwssNwssPut>>, TError,{data: Nwss}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNwssNwssPut>>,
        TError,
        {data: Nwss},
        TContext
      > => {
      return useMutation(getUpdateNwssNwssPutMutationOptions(options), queryClient);
    }

/**
 * Delete an existing NWSS entry.
 * @summary Delete NWSS metrics
 */
export const deleteNwssNwssDelete = (
    nwssKey: NwssKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/nwss`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: nwssKey, signal
    },
      options);
    }



export const getDeleteNwssNwssDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNwssNwssDelete>>, TError,{data: NwssKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNwssNwssDelete>>, TError,{data: NwssKey}, TContext> => {

const mutationKey = ['deleteNwssNwssDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNwssNwssDelete>>, {data: NwssKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteNwssNwssDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNwssNwssDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNwssNwssDelete>>>
    export type DeleteNwssNwssDeleteMutationBody = NwssKey
    export type DeleteNwssNwssDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete NWSS metrics
 */
export const useDeleteNwssNwssDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNwssNwssDelete>>, TError,{data: NwssKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNwssNwssDelete>>,
        TError,
        {data: NwssKey},
        TContext
      > => {
      return useMutation(getDeleteNwssNwssDeleteMutationOptions(options), queryClient);
    }

/**
 * Return the latest ``sample_collect_date`` on the raw NWSS table.

Drives the FE header's data-freshness indicator.
 * @summary Latest CDC sample date known to TA
 */
export const latestSampleCollectedAtNwssLatestSampleCollectedAtGet = (

 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<LatestNwssSample>(
      {url: `/nwss/latest_sample_collected_at`, method: 'GET', signal
    },
      options);
    }




export const getLatestSampleCollectedAtNwssLatestSampleCollectedAtGetQueryKey = () => {
    return [
    `/nwss/latest_sample_collected_at`
    ] as const;
    }


export const getLatestSampleCollectedAtNwssLatestSampleCollectedAtGetQueryOptions = <TData = Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError = unknown>( options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getLatestSampleCollectedAtNwssLatestSampleCollectedAtGetQueryKey();



    const queryFn: QueryFunction<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>> = ({ signal }) => latestSampleCollectedAtNwssLatestSampleCollectedAtGet(requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type LatestSampleCollectedAtNwssLatestSampleCollectedAtGetQueryResult = NonNullable<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>>
export type LatestSampleCollectedAtNwssLatestSampleCollectedAtGetQueryError = unknown


export function useLatestSampleCollectedAtNwssLatestSampleCollectedAtGet<TData = Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError = unknown>(
  options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>,
          TError,
          Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useLatestSampleCollectedAtNwssLatestSampleCollectedAtGet<TData = Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>,
          TError,
          Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useLatestSampleCollectedAtNwssLatestSampleCollectedAtGet<TData = Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Latest CDC sample date known to TA
 */

export function useLatestSampleCollectedAtNwssLatestSampleCollectedAtGet<TData = Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError = unknown>(
  options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof latestSampleCollectedAtNwssLatestSampleCollectedAtGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getLatestSampleCollectedAtNwssLatestSampleCollectedAtGetQueryOptions(options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Return NWSS metrics with optional filters.
 * @summary List NWSS metrics
 */
export const listNwssNwssListGet = (
    params?: ListNwssNwssListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<Nwss[]>(
      {url: `/nwss/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNwssNwssListGetQueryKey = (params?: ListNwssNwssListGetParams,) => {
    return [
    `/nwss/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNwssNwssListGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssNwssListGet>>, TError = HTTPValidationError>(params?: ListNwssNwssListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssNwssListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssNwssListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssNwssListGet>>> = ({ signal }) => listNwssNwssListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssNwssListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssNwssListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssNwssListGet>>>
export type ListNwssNwssListGetQueryError = HTTPValidationError


export function useListNwssNwssListGet<TData = Awaited<ReturnType<typeof listNwssNwssListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListNwssNwssListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssNwssListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssNwssListGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssNwssListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssNwssListGet<TData = Awaited<ReturnType<typeof listNwssNwssListGet>>, TError = HTTPValidationError>(
 params?: ListNwssNwssListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssNwssListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssNwssListGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssNwssListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssNwssListGet<TData = Awaited<ReturnType<typeof listNwssNwssListGet>>, TError = HTTPValidationError>(
 params?: ListNwssNwssListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssNwssListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List NWSS metrics
 */

export function useListNwssNwssListGet<TData = Awaited<ReturnType<typeof listNwssNwssListGet>>, TError = HTTPValidationError>(
 params?: ListNwssNwssListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssNwssListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssNwssListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Bulk upsert anomaly event clusters, keyed on ``event_id``.
 * @summary Create or update NWSS anomaly event clusters
 */
export const upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost = (
    nwssAnomalyEventClusterUpsertRequest: NwssAnomalyEventClusterUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssAnomalyEventClusterUpsertResponse>(
      {url: `/nwss/anomaly-event-clusters/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: nwssAnomalyEventClusterUpsertRequest, signal
    },
      options);
    }



export const getUpsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost>>, TError,{data: NwssAnomalyEventClusterUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost>>, TError,{data: NwssAnomalyEventClusterUpsertRequest}, TContext> => {

const mutationKey = ['upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost>>, {data: NwssAnomalyEventClusterUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost>>>
    export type UpsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPostMutationBody = NwssAnomalyEventClusterUpsertRequest
    export type UpsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update NWSS anomaly event clusters
 */
export const useUpsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost>>, TError,{data: NwssAnomalyEventClusterUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPost>>,
        TError,
        {data: NwssAnomalyEventClusterUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertNwssAnomalyEventClustersNwssAnomalyEventClustersUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing anomaly event cluster by ``event_id``.
 * @summary Update a NWSS anomaly event cluster
 */
export const updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut = (
    nwssAnomalyEventCluster: NwssAnomalyEventCluster,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssAnomalyEventCluster>(
      {url: `/nwss/anomaly-event-clusters`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: nwssAnomalyEventCluster, signal
    },
      options);
    }



export const getUpdateNwssAnomalyEventClusterNwssAnomalyEventClustersPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut>>, TError,{data: NwssAnomalyEventCluster}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut>>, TError,{data: NwssAnomalyEventCluster}, TContext> => {

const mutationKey = ['updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut>>, {data: NwssAnomalyEventCluster}> = (props) => {
          const {data} = props ?? {};

          return  updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNwssAnomalyEventClusterNwssAnomalyEventClustersPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut>>>
    export type UpdateNwssAnomalyEventClusterNwssAnomalyEventClustersPutMutationBody = NwssAnomalyEventCluster
    export type UpdateNwssAnomalyEventClusterNwssAnomalyEventClustersPutMutationError = HTTPValidationError

    /**
 * @summary Update a NWSS anomaly event cluster
 */
export const useUpdateNwssAnomalyEventClusterNwssAnomalyEventClustersPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut>>, TError,{data: NwssAnomalyEventCluster}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNwssAnomalyEventClusterNwssAnomalyEventClustersPut>>,
        TError,
        {data: NwssAnomalyEventCluster},
        TContext
      > => {
      return useMutation(getUpdateNwssAnomalyEventClusterNwssAnomalyEventClustersPutMutationOptions(options), queryClient);
    }

/**
 * Delete an anomaly event cluster by ``event_id``.
 * @summary Delete a NWSS anomaly event cluster
 */
export const deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete = (
    nwssAnomalyEventClusterKey: NwssAnomalyEventClusterKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/nwss/anomaly-event-clusters`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: nwssAnomalyEventClusterKey, signal
    },
      options);
    }



export const getDeleteNwssAnomalyEventClusterNwssAnomalyEventClustersDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete>>, TError,{data: NwssAnomalyEventClusterKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete>>, TError,{data: NwssAnomalyEventClusterKey}, TContext> => {

const mutationKey = ['deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete>>, {data: NwssAnomalyEventClusterKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNwssAnomalyEventClusterNwssAnomalyEventClustersDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete>>>
    export type DeleteNwssAnomalyEventClusterNwssAnomalyEventClustersDeleteMutationBody = NwssAnomalyEventClusterKey
    export type DeleteNwssAnomalyEventClusterNwssAnomalyEventClustersDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete a NWSS anomaly event cluster
 */
export const useDeleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete>>, TError,{data: NwssAnomalyEventClusterKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNwssAnomalyEventClusterNwssAnomalyEventClustersDelete>>,
        TError,
        {data: NwssAnomalyEventClusterKey},
        TContext
      > => {
      return useMutation(getDeleteNwssAnomalyEventClusterNwssAnomalyEventClustersDeleteMutationOptions(options), queryClient);
    }

/**
 * Return anomaly event clusters with optional filters and sorting.

Sorting uses paired ``sort_by`` / ``sort_direction`` query params (repeatable for
multi-column ordering). Default is ``event_start_date DESC``.
 * @summary List NWSS anomaly event clusters
 */
export const listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet = (
    bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet?: BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params?: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssAnomalyEventCluster[]>(
      {url: `/nwss/anomaly-event-clusters/list`, method: 'GET',
      headers: {'Content-Type': 'application/json', },
        params, signal
    },
      options);
    }




export const getListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetQueryKey = (bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet?: BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params?: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams,) => {
    return [
    `/nwss/anomaly-event-clusters/list`, ...(params ? [params] : []), bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet
    ] as const;
    }


export const getListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError = HTTPValidationError>(bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet?: BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params?: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetQueryKey(bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>> = ({ signal }) => listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet(bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>>
export type ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetQueryError = HTTPValidationError


export function useListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet<TData = Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError = HTTPValidationError>(
 bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet: undefined |  BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params: undefined |  ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet<TData = Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError = HTTPValidationError>(
 bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet?: BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params?: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet<TData = Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError = HTTPValidationError>(
 bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet?: BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params?: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List NWSS anomaly event clusters
 */

export function useListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet<TData = Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError = HTTPValidationError>(
 bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet?: BodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,
    params?: ListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssAnomalyEventClustersNwssAnomalyEventClustersListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssAnomalyEventClustersNwssAnomalyEventClustersListGetQueryOptions(bodyListNwssAnomalyEventClustersNwssAnomalyEventClustersListGet,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Distinct NWSS filter options for a detection date window
 */
export const listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet = (
    params?: ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssBiothreatSignalsFacets>(
      {url: `/nwss/biothreat-signals/facets`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetQueryKey = (params?: ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams,) => {
    return [
    `/nwss/biothreat-signals/facets`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(params?: ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>> = ({ signal }) => listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>>
export type ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetQueryError = HTTPValidationError


export function useListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params: undefined |  ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params?: ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params?: ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Distinct NWSS filter options for a detection date window
 */

export function useListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params?: ListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssBiothreatSignalsFacetsNwssBiothreatSignalsFacetsGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary List precomputed NWSS biothreat signals
 */
export const listNwssBiothreatSignalsNwssBiothreatSignalsGet = (
    params?: ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiothreatSignalList>(
      {url: `/nwss/biothreat-signals`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNwssBiothreatSignalsNwssBiothreatSignalsGetQueryKey = (params?: ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams,) => {
    return [
    `/nwss/biothreat-signals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNwssBiothreatSignalsNwssBiothreatSignalsGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError = HTTPValidationError>(params?: ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssBiothreatSignalsNwssBiothreatSignalsGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>> = ({ signal }) => listNwssBiothreatSignalsNwssBiothreatSignalsGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssBiothreatSignalsNwssBiothreatSignalsGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>>
export type ListNwssBiothreatSignalsNwssBiothreatSignalsGetQueryError = HTTPValidationError


export function useListNwssBiothreatSignalsNwssBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params: undefined |  ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssBiothreatSignalsNwssBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params?: ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssBiothreatSignalsNwssBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params?: ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List precomputed NWSS biothreat signals
 */

export function useListNwssBiothreatSignalsNwssBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params?: ListNwssBiothreatSignalsNwssBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBiothreatSignalsNwssBiothreatSignalsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssBiothreatSignalsNwssBiothreatSignalsGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get one precomputed NWSS biothreat signal by event id
 */
export const getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet = (
    eventId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiothreatSignal>(
      {url: `/nwss/biothreat-signals/${eventId}`, method: 'GET', signal
    },
      options);
    }




export const getGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGetQueryKey = (eventId: string,) => {
    return [
    `/nwss/biothreat-signals/${eventId}`
    ] as const;
    }


export const getGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError = HTTPValidationError>(eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGetQueryKey(eventId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>> = ({ signal }) => getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet(eventId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: eventId !== null && eventId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetNwssBiothreatSignalNwssBiothreatSignalsEventIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>>
export type GetNwssBiothreatSignalNwssBiothreatSignalsEventIdGetQueryError = HTTPValidationError


export function useGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGet<TData = Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>,
          TError,
          Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGet<TData = Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>,
          TError,
          Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGet<TData = Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get one precomputed NWSS biothreat signal by event id
 */

export function useGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGet<TData = Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssBiothreatSignalNwssBiothreatSignalsEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetNwssBiothreatSignalNwssBiothreatSignalsEventIdGetQueryOptions(eventId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Return the raw NWSS samples a precomputed event was derived from.

Loads the anomaly-event cluster by ``event_id``, then returns the full
sampling history (detections *and* non-detects) for the sites that
produced its member detections, across the event window padded by
``context_days`` on each side. Each row is tagged ``is_event_member``
(one of the cluster's derived detections) and ``is_detected`` (a positive
detection, mirroring ``nwss_binary_detection``: ``pcr_target_detect ==
"yes"``). Set ``include_non_detects=false`` for the detect-only subset.

``member_count`` counts event members among returned rows only (after
``include_non_detects`` filtering). Up to ``SAMPLE_ROW_CAP`` rows are
returned; detections are retained before non-detect context rows.
 * @summary Raw NWSS sampling history underlying a precomputed signal
 */
export const listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet = (
    eventId: string,
    params?: ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssSignalSampleList>(
      {url: `/nwss/biothreat-signals/${eventId}/samples`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetQueryKey = (eventId: string,
    params?: ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams,) => {
    return [
    `/nwss/biothreat-signals/${eventId}/samples`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError = HTTPValidationError>(eventId: string,
    params?: ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetQueryKey(eventId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>> = ({ signal }) => listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet(eventId,params, requestOptions, signal);





   return  { queryKey, queryFn, enabled: eventId !== null && eventId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>>
export type ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetQueryError = HTTPValidationError


export function useListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet<TData = Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError = HTTPValidationError>(
 eventId: string,
    params: undefined |  ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet<TData = Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError = HTTPValidationError>(
 eventId: string,
    params?: ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet<TData = Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError = HTTPValidationError>(
 eventId: string,
    params?: ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Raw NWSS sampling history underlying a precomputed signal
 */

export function useListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet<TData = Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError = HTTPValidationError>(
 eventId: string,
    params?: ListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssSignalSamplesNwssBiothreatSignalsEventIdSamplesGetQueryOptions(eventId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Bulk upsert binary-detection rows keyed on ``(pathogen, record_id)``.
 * @summary Create or update NWSS binary-detection rows
 */
export const upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost = (
    nwssBinaryDetectionUpsertRequest: NwssBinaryDetectionUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssBinaryDetection[]>(
      {url: `/nwss/binary-detection/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: nwssBinaryDetectionUpsertRequest, signal
    },
      options);
    }



export const getUpsertNwssBinaryDetectionNwssBinaryDetectionUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost>>, TError,{data: NwssBinaryDetectionUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost>>, TError,{data: NwssBinaryDetectionUpsertRequest}, TContext> => {

const mutationKey = ['upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost>>, {data: NwssBinaryDetectionUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertNwssBinaryDetectionNwssBinaryDetectionUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost>>>
    export type UpsertNwssBinaryDetectionNwssBinaryDetectionUpsertPostMutationBody = NwssBinaryDetectionUpsertRequest
    export type UpsertNwssBinaryDetectionNwssBinaryDetectionUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update NWSS binary-detection rows
 */
export const useUpsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost>>, TError,{data: NwssBinaryDetectionUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertNwssBinaryDetectionNwssBinaryDetectionUpsertPost>>,
        TError,
        {data: NwssBinaryDetectionUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertNwssBinaryDetectionNwssBinaryDetectionUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing binary-detection row by ``(pathogen, record_id)``.
 * @summary Update a NWSS binary-detection row
 */
export const updateNwssBinaryDetectionNwssBinaryDetectionPut = (
    nwssBinaryDetection: NwssBinaryDetection,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssBinaryDetection>(
      {url: `/nwss/binary-detection`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: nwssBinaryDetection, signal
    },
      options);
    }



export const getUpdateNwssBinaryDetectionNwssBinaryDetectionPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNwssBinaryDetectionNwssBinaryDetectionPut>>, TError,{data: NwssBinaryDetection}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateNwssBinaryDetectionNwssBinaryDetectionPut>>, TError,{data: NwssBinaryDetection}, TContext> => {

const mutationKey = ['updateNwssBinaryDetectionNwssBinaryDetectionPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateNwssBinaryDetectionNwssBinaryDetectionPut>>, {data: NwssBinaryDetection}> = (props) => {
          const {data} = props ?? {};

          return  updateNwssBinaryDetectionNwssBinaryDetectionPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateNwssBinaryDetectionNwssBinaryDetectionPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateNwssBinaryDetectionNwssBinaryDetectionPut>>>
    export type UpdateNwssBinaryDetectionNwssBinaryDetectionPutMutationBody = NwssBinaryDetection
    export type UpdateNwssBinaryDetectionNwssBinaryDetectionPutMutationError = HTTPValidationError

    /**
 * @summary Update a NWSS binary-detection row
 */
export const useUpdateNwssBinaryDetectionNwssBinaryDetectionPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateNwssBinaryDetectionNwssBinaryDetectionPut>>, TError,{data: NwssBinaryDetection}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateNwssBinaryDetectionNwssBinaryDetectionPut>>,
        TError,
        {data: NwssBinaryDetection},
        TContext
      > => {
      return useMutation(getUpdateNwssBinaryDetectionNwssBinaryDetectionPutMutationOptions(options), queryClient);
    }

/**
 * @summary Delete a NWSS binary-detection row
 */
export const deleteNwssBinaryDetectionNwssBinaryDetectionDelete = (
    nwssBinaryDetectionKey: NwssBinaryDetectionKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/nwss/binary-detection`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: nwssBinaryDetectionKey, signal
    },
      options);
    }



export const getDeleteNwssBinaryDetectionNwssBinaryDetectionDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNwssBinaryDetectionNwssBinaryDetectionDelete>>, TError,{data: NwssBinaryDetectionKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteNwssBinaryDetectionNwssBinaryDetectionDelete>>, TError,{data: NwssBinaryDetectionKey}, TContext> => {

const mutationKey = ['deleteNwssBinaryDetectionNwssBinaryDetectionDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteNwssBinaryDetectionNwssBinaryDetectionDelete>>, {data: NwssBinaryDetectionKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteNwssBinaryDetectionNwssBinaryDetectionDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteNwssBinaryDetectionNwssBinaryDetectionDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteNwssBinaryDetectionNwssBinaryDetectionDelete>>>
    export type DeleteNwssBinaryDetectionNwssBinaryDetectionDeleteMutationBody = NwssBinaryDetectionKey
    export type DeleteNwssBinaryDetectionNwssBinaryDetectionDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete a NWSS binary-detection row
 */
export const useDeleteNwssBinaryDetectionNwssBinaryDetectionDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteNwssBinaryDetectionNwssBinaryDetectionDelete>>, TError,{data: NwssBinaryDetectionKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteNwssBinaryDetectionNwssBinaryDetectionDelete>>,
        TError,
        {data: NwssBinaryDetectionKey},
        TContext
      > => {
      return useMutation(getDeleteNwssBinaryDetectionNwssBinaryDetectionDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary List NWSS binary-detection rows
 */
export const listNwssBinaryDetectionNwssBinaryDetectionListGet = (
    params?: ListNwssBinaryDetectionNwssBinaryDetectionListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<NwssBinaryDetection[]>(
      {url: `/nwss/binary-detection/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNwssBinaryDetectionNwssBinaryDetectionListGetQueryKey = (params?: ListNwssBinaryDetectionNwssBinaryDetectionListGetParams,) => {
    return [
    `/nwss/binary-detection/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNwssBinaryDetectionNwssBinaryDetectionListGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError = HTTPValidationError>(params?: ListNwssBinaryDetectionNwssBinaryDetectionListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssBinaryDetectionNwssBinaryDetectionListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>> = ({ signal }) => listNwssBinaryDetectionNwssBinaryDetectionListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssBinaryDetectionNwssBinaryDetectionListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>>
export type ListNwssBinaryDetectionNwssBinaryDetectionListGetQueryError = HTTPValidationError


export function useListNwssBinaryDetectionNwssBinaryDetectionListGet<TData = Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListNwssBinaryDetectionNwssBinaryDetectionListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssBinaryDetectionNwssBinaryDetectionListGet<TData = Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError = HTTPValidationError>(
 params?: ListNwssBinaryDetectionNwssBinaryDetectionListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssBinaryDetectionNwssBinaryDetectionListGet<TData = Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError = HTTPValidationError>(
 params?: ListNwssBinaryDetectionNwssBinaryDetectionListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List NWSS binary-detection rows
 */

export function useListNwssBinaryDetectionNwssBinaryDetectionListGet<TData = Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError = HTTPValidationError>(
 params?: ListNwssBinaryDetectionNwssBinaryDetectionListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssBinaryDetectionNwssBinaryDetectionListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssBinaryDetectionNwssBinaryDetectionListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * List biothreat signals derived on the fly from NWSS binary detection.

Each signal represents a gaps-and-islands cluster of consecutive
rare-target (measles / H5 / mpox / NVO) ``is_detected = TRUE`` rows
within ``MAX_EVENT_GAP_DAYS`` of each other, grouped by disease and
geography. Response envelope mirrors phoenix ``/api/v1/ai/candidates/``
(``{count, results}``) so the FE can consume both sources uniformly;
``count`` is the pre-page total.

``include_ended`` defaults to ``False`` — ended events are hidden
because the inbox surface shows active threats only.
 * @summary List NWSS biothreat signals
 */
export const listNwssEventsNwssEventsGet = (
    params?: ListNwssEventsNwssEventsGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiothreatSignalList>(
      {url: `/nwss/events`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListNwssEventsNwssEventsGetQueryKey = (params?: ListNwssEventsNwssEventsGetParams,) => {
    return [
    `/nwss/events`, ...(params ? [params] : [])
    ] as const;
    }


export const getListNwssEventsNwssEventsGetQueryOptions = <TData = Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError = HTTPValidationError>(params?: ListNwssEventsNwssEventsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListNwssEventsNwssEventsGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>> = ({ signal }) => listNwssEventsNwssEventsGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListNwssEventsNwssEventsGetQueryResult = NonNullable<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>>
export type ListNwssEventsNwssEventsGetQueryError = HTTPValidationError


export function useListNwssEventsNwssEventsGet<TData = Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError = HTTPValidationError>(
 params: undefined |  ListNwssEventsNwssEventsGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssEventsNwssEventsGet<TData = Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError = HTTPValidationError>(
 params?: ListNwssEventsNwssEventsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>,
          TError,
          Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListNwssEventsNwssEventsGet<TData = Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError = HTTPValidationError>(
 params?: ListNwssEventsNwssEventsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List NWSS biothreat signals
 */

export function useListNwssEventsNwssEventsGet<TData = Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError = HTTPValidationError>(
 params?: ListNwssEventsNwssEventsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listNwssEventsNwssEventsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListNwssEventsNwssEventsGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Return the signal plus the raw binary-detection rows that composed it.

``event_id`` is a content hash of the cluster's current member set. If
CDC publishes a new detection for the same cluster between the list
fetch and the detail click, the id goes stale. The FE should refresh
the list on 404 and retry with the new id.

``start_date`` should be the same value the FE passed to the list
endpoint. It anchors the scan window (``start_date - INITIAL_LOOKBACK_DAYS``
to today) so the detail lookup sees the same member set the list used
to compute ``event_id``. Omitting it defaults to a 336-day scan,
matching the list endpoint's default.
 * @summary Fetch a single NWSS biothreat signal with its underlying detections
 */
export const getNwssEventDetailNwssEventsEventIdGet = (
    eventId: string,
    params?: GetNwssEventDetailNwssEventsEventIdGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiothreatSignalDetail>(
      {url: `/nwss/events/${eventId}`, method: 'GET',
        params, signal
    },
      options);
    }




export const getGetNwssEventDetailNwssEventsEventIdGetQueryKey = (eventId: string,
    params?: GetNwssEventDetailNwssEventsEventIdGetParams,) => {
    return [
    `/nwss/events/${eventId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetNwssEventDetailNwssEventsEventIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError = HTTPValidationError>(eventId: string,
    params?: GetNwssEventDetailNwssEventsEventIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetNwssEventDetailNwssEventsEventIdGetQueryKey(eventId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>> = ({ signal }) => getNwssEventDetailNwssEventsEventIdGet(eventId,params, requestOptions, signal);





   return  { queryKey, queryFn, enabled: eventId !== null && eventId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetNwssEventDetailNwssEventsEventIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>>
export type GetNwssEventDetailNwssEventsEventIdGetQueryError = HTTPValidationError


export function useGetNwssEventDetailNwssEventsEventIdGet<TData = Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string,
    params: undefined |  GetNwssEventDetailNwssEventsEventIdGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>,
          TError,
          Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNwssEventDetailNwssEventsEventIdGet<TData = Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string,
    params?: GetNwssEventDetailNwssEventsEventIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>,
          TError,
          Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetNwssEventDetailNwssEventsEventIdGet<TData = Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string,
    params?: GetNwssEventDetailNwssEventsEventIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Fetch a single NWSS biothreat signal with its underlying detections
 */

export function useGetNwssEventDetailNwssEventsEventIdGet<TData = Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError = HTTPValidationError>(
 eventId: string,
    params?: GetNwssEventDetailNwssEventsEventIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getNwssEventDetailNwssEventsEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetNwssEventDetailNwssEventsEventIdGetQueryOptions(eventId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Upsert wastewater PCR analysis items.
 * @summary Create wastewater PCR analysis items
 */
export const createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost = (
    wastewaterPCRAnalysisUpsertRequest: WastewaterPCRAnalysisUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRAnalysis[]>(
      {url: `/wastewater/pcr-analysis/create`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRAnalysisUpsertRequest, signal
    },
      options);
    }



export const getCreateWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost>>, TError,{data: WastewaterPCRAnalysisUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost>>, TError,{data: WastewaterPCRAnalysisUpsertRequest}, TContext> => {

const mutationKey = ['createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost>>, {data: WastewaterPCRAnalysisUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost>>>
    export type CreateWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePostMutationBody = WastewaterPCRAnalysisUpsertRequest
    export type CreateWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePostMutationError = HTTPValidationError

    /**
 * @summary Create wastewater PCR analysis items
 */
export const useCreateWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost>>, TError,{data: WastewaterPCRAnalysisUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePost>>,
        TError,
        {data: WastewaterPCRAnalysisUpsertRequest},
        TContext
      > => {
      return useMutation(getCreateWastewaterPcrAnalysisWastewaterPcrAnalysisCreatePostMutationOptions(options), queryClient);
    }

/**
 * Upsert wastewater PCR analysis items.
 * @summary Create wastewater PCR analysis items
 */
export const upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost = (
    wastewaterPCRAnalysisUpsertRequest: WastewaterPCRAnalysisUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRAnalysis[]>(
      {url: `/wastewater/pcr-analysis/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRAnalysisUpsertRequest, signal
    },
      options);
    }



export const getUpsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost>>, TError,{data: WastewaterPCRAnalysisUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost>>, TError,{data: WastewaterPCRAnalysisUpsertRequest}, TContext> => {

const mutationKey = ['upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost>>, {data: WastewaterPCRAnalysisUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost>>>
    export type UpsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPostMutationBody = WastewaterPCRAnalysisUpsertRequest
    export type UpsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create wastewater PCR analysis items
 */
export const useUpsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost>>, TError,{data: WastewaterPCRAnalysisUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPost>>,
        TError,
        {data: WastewaterPCRAnalysisUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertWastewaterPcrAnalysisWastewaterPcrAnalysisUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing wastewater PCR analysis item.
 * @summary Update wastewater PCR analysis item
 */
export const updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut = (
    wastewaterPCRAnalysis: WastewaterPCRAnalysis,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRAnalysis>(
      {url: `/wastewater/pcr-analysis`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRAnalysis, signal
    },
      options);
    }



export const getUpdateWastewaterPcrAnalysisWastewaterPcrAnalysisPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut>>, TError,{data: WastewaterPCRAnalysis}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut>>, TError,{data: WastewaterPCRAnalysis}, TContext> => {

const mutationKey = ['updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut>>, {data: WastewaterPCRAnalysis}> = (props) => {
          const {data} = props ?? {};

          return  updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWastewaterPcrAnalysisWastewaterPcrAnalysisPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut>>>
    export type UpdateWastewaterPcrAnalysisWastewaterPcrAnalysisPutMutationBody = WastewaterPCRAnalysis
    export type UpdateWastewaterPcrAnalysisWastewaterPcrAnalysisPutMutationError = HTTPValidationError

    /**
 * @summary Update wastewater PCR analysis item
 */
export const useUpdateWastewaterPcrAnalysisWastewaterPcrAnalysisPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut>>, TError,{data: WastewaterPCRAnalysis}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateWastewaterPcrAnalysisWastewaterPcrAnalysisPut>>,
        TError,
        {data: WastewaterPCRAnalysis},
        TContext
      > => {
      return useMutation(getUpdateWastewaterPcrAnalysisWastewaterPcrAnalysisPutMutationOptions(options), queryClient);
    }

/**
 * Delete an existing wastewater PCR analysis measurement.
 * @summary Delete wastewater PCR measurement
 */
export const deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete = (
    wastewaterPCRAnalysisKey: WastewaterPCRAnalysisKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/wastewater/pcr-analysis`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRAnalysisKey, signal
    },
      options);
    }



export const getDeleteWastewaterPcrAnalysisWastewaterPcrAnalysisDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete>>, TError,{data: WastewaterPCRAnalysisKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete>>, TError,{data: WastewaterPCRAnalysisKey}, TContext> => {

const mutationKey = ['deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete>>, {data: WastewaterPCRAnalysisKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWastewaterPcrAnalysisWastewaterPcrAnalysisDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete>>>
    export type DeleteWastewaterPcrAnalysisWastewaterPcrAnalysisDeleteMutationBody = WastewaterPCRAnalysisKey
    export type DeleteWastewaterPcrAnalysisWastewaterPcrAnalysisDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete wastewater PCR measurement
 */
export const useDeleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete>>, TError,{data: WastewaterPCRAnalysisKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWastewaterPcrAnalysisWastewaterPcrAnalysisDelete>>,
        TError,
        {data: WastewaterPCRAnalysisKey},
        TContext
      > => {
      return useMutation(getDeleteWastewaterPcrAnalysisWastewaterPcrAnalysisDeleteMutationOptions(options), queryClient);
    }

/**
 * Return wastewater PCR analysis items with optional filters.
 * @summary List wastewater PCR analysis items
 */
export const listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet = (
    params?: ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRAnalysis[]>(
      {url: `/wastewater/pcr-analysis/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetQueryKey = (params?: ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams,) => {
    return [
    `/wastewater/pcr-analysis/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetQueryOptions = <TData = Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError = HTTPValidationError>(params?: ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>> = ({ signal }) => listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>>
export type ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetQueryError = HTTPValidationError


export function useListWastewaterPcrAnalysisWastewaterPcrAnalysisListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>,
          TError,
          Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWastewaterPcrAnalysisWastewaterPcrAnalysisListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError = HTTPValidationError>(
 params?: ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>,
          TError,
          Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWastewaterPcrAnalysisWastewaterPcrAnalysisListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError = HTTPValidationError>(
 params?: ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List wastewater PCR analysis items
 */

export function useListWastewaterPcrAnalysisWastewaterPcrAnalysisListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError = HTTPValidationError>(
 params?: ListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrAnalysisWastewaterPcrAnalysisListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWastewaterPcrAnalysisWastewaterPcrAnalysisListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Upsert wastewater PCR activity level items.
 * @summary Create wastewater PCR activity level items
 */
export const createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost = (
    wastewaterPCRActivityLevelUpsertRequest: WastewaterPCRActivityLevelUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRActivityLevel[]>(
      {url: `/wastewater/pcr-activity-level/create`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRActivityLevelUpsertRequest, signal
    },
      options);
    }



export const getCreateWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost>>, TError,{data: WastewaterPCRActivityLevelUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost>>, TError,{data: WastewaterPCRActivityLevelUpsertRequest}, TContext> => {

const mutationKey = ['createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost>>, {data: WastewaterPCRActivityLevelUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePostMutationResult = NonNullable<Awaited<ReturnType<typeof createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost>>>
    export type CreateWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePostMutationBody = WastewaterPCRActivityLevelUpsertRequest
    export type CreateWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePostMutationError = HTTPValidationError

    /**
 * @summary Create wastewater PCR activity level items
 */
export const useCreateWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost>>, TError,{data: WastewaterPCRActivityLevelUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePost>>,
        TError,
        {data: WastewaterPCRActivityLevelUpsertRequest},
        TContext
      > => {
      return useMutation(getCreateWastewaterPcrActivityLevelWastewaterPcrActivityLevelCreatePostMutationOptions(options), queryClient);
    }

/**
 * Upsert wastewater PCR activity level items.
 * @summary Create wastewater PCR activity level items
 */
export const upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost = (
    wastewaterPCRActivityLevelUpsertRequest: WastewaterPCRActivityLevelUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRActivityLevel[]>(
      {url: `/wastewater/pcr-activity-level/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRActivityLevelUpsertRequest, signal
    },
      options);
    }



export const getUpsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost>>, TError,{data: WastewaterPCRActivityLevelUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost>>, TError,{data: WastewaterPCRActivityLevelUpsertRequest}, TContext> => {

const mutationKey = ['upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost>>, {data: WastewaterPCRActivityLevelUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost>>>
    export type UpsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPostMutationBody = WastewaterPCRActivityLevelUpsertRequest
    export type UpsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create wastewater PCR activity level items
 */
export const useUpsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost>>, TError,{data: WastewaterPCRActivityLevelUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPost>>,
        TError,
        {data: WastewaterPCRActivityLevelUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertWastewaterPcrActivityLevelWastewaterPcrActivityLevelUpsertPostMutationOptions(options), queryClient);
    }

/**
 * Update an existing wastewater PCR activity level item.
 * @summary Update wastewater PCR activity level item
 */
export const updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut = (
    wastewaterPCRActivityLevel: WastewaterPCRActivityLevel,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRActivityLevel>(
      {url: `/wastewater/pcr-activity-level`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRActivityLevel, signal
    },
      options);
    }



export const getUpdateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut>>, TError,{data: WastewaterPCRActivityLevel}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut>>, TError,{data: WastewaterPCRActivityLevel}, TContext> => {

const mutationKey = ['updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut>>, {data: WastewaterPCRActivityLevel}> = (props) => {
          const {data} = props ?? {};

          return  updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut>>>
    export type UpdateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPutMutationBody = WastewaterPCRActivityLevel
    export type UpdateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPutMutationError = HTTPValidationError

    /**
 * @summary Update wastewater PCR activity level item
 */
export const useUpdateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut>>, TError,{data: WastewaterPCRActivityLevel}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPut>>,
        TError,
        {data: WastewaterPCRActivityLevel},
        TContext
      > => {
      return useMutation(getUpdateWastewaterPcrActivityLevelWastewaterPcrActivityLevelPutMutationOptions(options), queryClient);
    }

/**
 * Delete an existing wastewater PCR activity level measurement.
 * @summary Delete wastewater PCR activity level measurement
 */
export const deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete = (
    wastewaterPCRActivityLevelKey: WastewaterPCRActivityLevelKey,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/wastewater/pcr-activity-level`, method: 'DELETE',
      headers: {'Content-Type': 'application/json', },
      data: wastewaterPCRActivityLevelKey, signal
    },
      options);
    }



export const getDeleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete>>, TError,{data: WastewaterPCRActivityLevelKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete>>, TError,{data: WastewaterPCRActivityLevelKey}, TContext> => {

const mutationKey = ['deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete>>, {data: WastewaterPCRActivityLevelKey}> = (props) => {
          const {data} = props ?? {};

          return  deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete>>>
    export type DeleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDeleteMutationBody = WastewaterPCRActivityLevelKey
    export type DeleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete wastewater PCR activity level measurement
 */
export const useDeleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete>>, TError,{data: WastewaterPCRActivityLevelKey}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDelete>>,
        TError,
        {data: WastewaterPCRActivityLevelKey},
        TContext
      > => {
      return useMutation(getDeleteWastewaterPcrActivityLevelWastewaterPcrActivityLevelDeleteMutationOptions(options), queryClient);
    }

/**
 * Return wastewater PCR activity level items with optional filters.
 * @summary List wastewater PCR activity level items
 */
export const listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet = (
    params?: ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<WastewaterPCRActivityLevel[]>(
      {url: `/wastewater/pcr-activity-level/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetQueryKey = (params?: ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams,) => {
    return [
    `/wastewater/pcr-activity-level/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetQueryOptions = <TData = Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError = HTTPValidationError>(params?: ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>> = ({ signal }) => listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>>
export type ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetQueryError = HTTPValidationError


export function useListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>,
          TError,
          Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError = HTTPValidationError>(
 params?: ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>,
          TError,
          Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError = HTTPValidationError>(
 params?: ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List wastewater PCR activity level items
 */

export function useListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet<TData = Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError = HTTPValidationError>(
 params?: ListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListWastewaterPcrActivityLevelWastewaterPcrActivityLevelListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Create a new biomesh contact
 */
export const createContactBiomeshContactPost = (
    biomeshContactCreate: BiomeshContactCreate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshContactResponse>(
      {url: `/biomesh/contact`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshContactCreate, signal
    },
      options);
    }



export const getCreateContactBiomeshContactPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContactBiomeshContactPost>>, TError,{data: BiomeshContactCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createContactBiomeshContactPost>>, TError,{data: BiomeshContactCreate}, TContext> => {

const mutationKey = ['createContactBiomeshContactPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createContactBiomeshContactPost>>, {data: BiomeshContactCreate}> = (props) => {
          const {data} = props ?? {};

          return  createContactBiomeshContactPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateContactBiomeshContactPostMutationResult = NonNullable<Awaited<ReturnType<typeof createContactBiomeshContactPost>>>
    export type CreateContactBiomeshContactPostMutationBody = BiomeshContactCreate
    export type CreateContactBiomeshContactPostMutationError = HTTPValidationError

    /**
 * @summary Create a new biomesh contact
 */
export const useCreateContactBiomeshContactPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createContactBiomeshContactPost>>, TError,{data: BiomeshContactCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createContactBiomeshContactPost>>,
        TError,
        {data: BiomeshContactCreate},
        TContext
      > => {
      return useMutation(getCreateContactBiomeshContactPostMutationOptions(options), queryClient);
    }

/**
 * @summary Bulk upsert biomesh contacts
 */
export const upsertContactsBiomeshContactUpsertPost = (
    biomeshContactUpsertRequest: BiomeshContactUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<number>(
      {url: `/biomesh/contact/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshContactUpsertRequest, signal
    },
      options);
    }



export const getUpsertContactsBiomeshContactUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertContactsBiomeshContactUpsertPost>>, TError,{data: BiomeshContactUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertContactsBiomeshContactUpsertPost>>, TError,{data: BiomeshContactUpsertRequest}, TContext> => {

const mutationKey = ['upsertContactsBiomeshContactUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertContactsBiomeshContactUpsertPost>>, {data: BiomeshContactUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertContactsBiomeshContactUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertContactsBiomeshContactUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertContactsBiomeshContactUpsertPost>>>
    export type UpsertContactsBiomeshContactUpsertPostMutationBody = BiomeshContactUpsertRequest
    export type UpsertContactsBiomeshContactUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Bulk upsert biomesh contacts
 */
export const useUpsertContactsBiomeshContactUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertContactsBiomeshContactUpsertPost>>, TError,{data: BiomeshContactUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertContactsBiomeshContactUpsertPost>>,
        TError,
        {data: BiomeshContactUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertContactsBiomeshContactUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List biomesh contacts
 */
export const listContactsBiomeshContactListGet = (
    params?: ListContactsBiomeshContactListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshContactResponse[]>(
      {url: `/biomesh/contact/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListContactsBiomeshContactListGetQueryKey = (params?: ListContactsBiomeshContactListGetParams,) => {
    return [
    `/biomesh/contact/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListContactsBiomeshContactListGetQueryOptions = <TData = Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError = HTTPValidationError>(params?: ListContactsBiomeshContactListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListContactsBiomeshContactListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>> = ({ signal }) => listContactsBiomeshContactListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListContactsBiomeshContactListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>>
export type ListContactsBiomeshContactListGetQueryError = HTTPValidationError


export function useListContactsBiomeshContactListGet<TData = Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListContactsBiomeshContactListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>,
          TError,
          Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListContactsBiomeshContactListGet<TData = Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError = HTTPValidationError>(
 params?: ListContactsBiomeshContactListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>,
          TError,
          Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListContactsBiomeshContactListGet<TData = Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError = HTTPValidationError>(
 params?: ListContactsBiomeshContactListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List biomesh contacts
 */

export function useListContactsBiomeshContactListGet<TData = Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError = HTTPValidationError>(
 params?: ListContactsBiomeshContactListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listContactsBiomeshContactListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListContactsBiomeshContactListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get biomesh contact by ID
 */
export const getContactBiomeshContactContactIdGet = (
    contactId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshContactResponse>(
      {url: `/biomesh/contact/${contactId}`, method: 'GET', signal
    },
      options);
    }




export const getGetContactBiomeshContactContactIdGetQueryKey = (contactId: string,) => {
    return [
    `/biomesh/contact/${contactId}`
    ] as const;
    }


export const getGetContactBiomeshContactContactIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError = HTTPValidationError>(contactId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetContactBiomeshContactContactIdGetQueryKey(contactId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>> = ({ signal }) => getContactBiomeshContactContactIdGet(contactId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: contactId !== null && contactId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetContactBiomeshContactContactIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>>
export type GetContactBiomeshContactContactIdGetQueryError = HTTPValidationError


export function useGetContactBiomeshContactContactIdGet<TData = Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError = HTTPValidationError>(
 contactId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>,
          TError,
          Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetContactBiomeshContactContactIdGet<TData = Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError = HTTPValidationError>(
 contactId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>,
          TError,
          Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetContactBiomeshContactContactIdGet<TData = Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError = HTTPValidationError>(
 contactId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get biomesh contact by ID
 */

export function useGetContactBiomeshContactContactIdGet<TData = Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError = HTTPValidationError>(
 contactId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getContactBiomeshContactContactIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetContactBiomeshContactContactIdGetQueryOptions(contactId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Replace all scalar fields. Relationship ID lists use replace semantics: provide a list to set associations, [] to clear, or omit to leave unchanged.
 * @summary Update biomesh contact
 */
export const updateContactBiomeshContactContactIdPut = (
    contactId: string,
    biomeshContactUpdate: BiomeshContactUpdate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshContactResponse>(
      {url: `/biomesh/contact/${contactId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: biomeshContactUpdate, signal
    },
      options);
    }



export const getUpdateContactBiomeshContactContactIdPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContactBiomeshContactContactIdPut>>, TError,{contactId: string;data: BiomeshContactUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateContactBiomeshContactContactIdPut>>, TError,{contactId: string;data: BiomeshContactUpdate}, TContext> => {

const mutationKey = ['updateContactBiomeshContactContactIdPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateContactBiomeshContactContactIdPut>>, {contactId: string;data: BiomeshContactUpdate}> = (props) => {
          const {contactId,data} = props ?? {};

          return  updateContactBiomeshContactContactIdPut(contactId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateContactBiomeshContactContactIdPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateContactBiomeshContactContactIdPut>>>
    export type UpdateContactBiomeshContactContactIdPutMutationBody = BiomeshContactUpdate
    export type UpdateContactBiomeshContactContactIdPutMutationError = HTTPValidationError

    /**
 * @summary Update biomesh contact
 */
export const useUpdateContactBiomeshContactContactIdPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateContactBiomeshContactContactIdPut>>, TError,{contactId: string;data: BiomeshContactUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateContactBiomeshContactContactIdPut>>,
        TError,
        {contactId: string;data: BiomeshContactUpdate},
        TContext
      > => {
      return useMutation(getUpdateContactBiomeshContactContactIdPutMutationOptions(options), queryClient);
    }

/**
 * @summary Patch biomesh contact
 */
export const patchContactBiomeshContactContactIdPatch = (
    contactId: string,
    biomeshContactPatch: BiomeshContactPatch,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshContactResponse>(
      {url: `/biomesh/contact/${contactId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: biomeshContactPatch, signal
    },
      options);
    }



export const getPatchContactBiomeshContactContactIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchContactBiomeshContactContactIdPatch>>, TError,{contactId: string;data: BiomeshContactPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchContactBiomeshContactContactIdPatch>>, TError,{contactId: string;data: BiomeshContactPatch}, TContext> => {

const mutationKey = ['patchContactBiomeshContactContactIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchContactBiomeshContactContactIdPatch>>, {contactId: string;data: BiomeshContactPatch}> = (props) => {
          const {contactId,data} = props ?? {};

          return  patchContactBiomeshContactContactIdPatch(contactId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchContactBiomeshContactContactIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchContactBiomeshContactContactIdPatch>>>
    export type PatchContactBiomeshContactContactIdPatchMutationBody = BiomeshContactPatch
    export type PatchContactBiomeshContactContactIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch biomesh contact
 */
export const usePatchContactBiomeshContactContactIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchContactBiomeshContactContactIdPatch>>, TError,{contactId: string;data: BiomeshContactPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchContactBiomeshContactContactIdPatch>>,
        TError,
        {contactId: string;data: BiomeshContactPatch},
        TContext
      > => {
      return useMutation(getPatchContactBiomeshContactContactIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Delete biomesh contact
 */
export const deleteContactBiomeshContactContactIdDelete = (
    contactId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/biomesh/contact/${contactId}`, method: 'DELETE', signal
    },
      options);
    }



export const getDeleteContactBiomeshContactContactIdDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteContactBiomeshContactContactIdDelete>>, TError,{contactId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteContactBiomeshContactContactIdDelete>>, TError,{contactId: string}, TContext> => {

const mutationKey = ['deleteContactBiomeshContactContactIdDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteContactBiomeshContactContactIdDelete>>, {contactId: string}> = (props) => {
          const {contactId} = props ?? {};

          return  deleteContactBiomeshContactContactIdDelete(contactId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteContactBiomeshContactContactIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteContactBiomeshContactContactIdDelete>>>

    export type DeleteContactBiomeshContactContactIdDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete biomesh contact
 */
export const useDeleteContactBiomeshContactContactIdDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteContactBiomeshContactContactIdDelete>>, TError,{contactId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteContactBiomeshContactContactIdDelete>>,
        TError,
        {contactId: string},
        TContext
      > => {
      return useMutation(getDeleteContactBiomeshContactContactIdDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary Create a new biomesh event
 */
export const createEventBiomeshEventPost = (
    biomeshEventCreate: BiomeshEventCreate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshEventResponse>(
      {url: `/biomesh/event`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshEventCreate, signal
    },
      options);
    }



export const getCreateEventBiomeshEventPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventBiomeshEventPost>>, TError,{data: BiomeshEventCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createEventBiomeshEventPost>>, TError,{data: BiomeshEventCreate}, TContext> => {

const mutationKey = ['createEventBiomeshEventPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createEventBiomeshEventPost>>, {data: BiomeshEventCreate}> = (props) => {
          const {data} = props ?? {};

          return  createEventBiomeshEventPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateEventBiomeshEventPostMutationResult = NonNullable<Awaited<ReturnType<typeof createEventBiomeshEventPost>>>
    export type CreateEventBiomeshEventPostMutationBody = BiomeshEventCreate
    export type CreateEventBiomeshEventPostMutationError = HTTPValidationError

    /**
 * @summary Create a new biomesh event
 */
export const useCreateEventBiomeshEventPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createEventBiomeshEventPost>>, TError,{data: BiomeshEventCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createEventBiomeshEventPost>>,
        TError,
        {data: BiomeshEventCreate},
        TContext
      > => {
      return useMutation(getCreateEventBiomeshEventPostMutationOptions(options), queryClient);
    }

/**
 * @summary Bulk upsert biomesh events
 */
export const upsertEventsBiomeshEventUpsertPost = (
    biomeshEventUpsertRequest: BiomeshEventUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<number>(
      {url: `/biomesh/event/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshEventUpsertRequest, signal
    },
      options);
    }



export const getUpsertEventsBiomeshEventUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertEventsBiomeshEventUpsertPost>>, TError,{data: BiomeshEventUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertEventsBiomeshEventUpsertPost>>, TError,{data: BiomeshEventUpsertRequest}, TContext> => {

const mutationKey = ['upsertEventsBiomeshEventUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertEventsBiomeshEventUpsertPost>>, {data: BiomeshEventUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertEventsBiomeshEventUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertEventsBiomeshEventUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertEventsBiomeshEventUpsertPost>>>
    export type UpsertEventsBiomeshEventUpsertPostMutationBody = BiomeshEventUpsertRequest
    export type UpsertEventsBiomeshEventUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Bulk upsert biomesh events
 */
export const useUpsertEventsBiomeshEventUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertEventsBiomeshEventUpsertPost>>, TError,{data: BiomeshEventUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertEventsBiomeshEventUpsertPost>>,
        TError,
        {data: BiomeshEventUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertEventsBiomeshEventUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List biomesh events
 */
export const listEventsBiomeshEventListGet = (
    params?: ListEventsBiomeshEventListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshEventResponse[]>(
      {url: `/biomesh/event/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListEventsBiomeshEventListGetQueryKey = (params?: ListEventsBiomeshEventListGetParams,) => {
    return [
    `/biomesh/event/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListEventsBiomeshEventListGetQueryOptions = <TData = Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError = HTTPValidationError>(params?: ListEventsBiomeshEventListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListEventsBiomeshEventListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>> = ({ signal }) => listEventsBiomeshEventListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListEventsBiomeshEventListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>>
export type ListEventsBiomeshEventListGetQueryError = HTTPValidationError


export function useListEventsBiomeshEventListGet<TData = Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListEventsBiomeshEventListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>,
          TError,
          Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListEventsBiomeshEventListGet<TData = Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError = HTTPValidationError>(
 params?: ListEventsBiomeshEventListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>,
          TError,
          Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListEventsBiomeshEventListGet<TData = Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError = HTTPValidationError>(
 params?: ListEventsBiomeshEventListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List biomesh events
 */

export function useListEventsBiomeshEventListGet<TData = Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError = HTTPValidationError>(
 params?: ListEventsBiomeshEventListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listEventsBiomeshEventListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListEventsBiomeshEventListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get biomesh event by ID
 */
export const getEventBiomeshEventEventIdGet = (
    eventId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshEventResponse>(
      {url: `/biomesh/event/${eventId}`, method: 'GET', signal
    },
      options);
    }




export const getGetEventBiomeshEventEventIdGetQueryKey = (eventId: string,) => {
    return [
    `/biomesh/event/${eventId}`
    ] as const;
    }


export const getGetEventBiomeshEventEventIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError = HTTPValidationError>(eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetEventBiomeshEventEventIdGetQueryKey(eventId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>> = ({ signal }) => getEventBiomeshEventEventIdGet(eventId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: eventId !== null && eventId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetEventBiomeshEventEventIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>>
export type GetEventBiomeshEventEventIdGetQueryError = HTTPValidationError


export function useGetEventBiomeshEventEventIdGet<TData = Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>,
          TError,
          Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetEventBiomeshEventEventIdGet<TData = Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>,
          TError,
          Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetEventBiomeshEventEventIdGet<TData = Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get biomesh event by ID
 */

export function useGetEventBiomeshEventEventIdGet<TData = Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError = HTTPValidationError>(
 eventId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getEventBiomeshEventEventIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetEventBiomeshEventEventIdGetQueryOptions(eventId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Replace all scalar fields on the event.
 * @summary Update biomesh event
 */
export const updateEventBiomeshEventEventIdPut = (
    eventId: string,
    biomeshEventUpdate: BiomeshEventUpdate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshEventResponse>(
      {url: `/biomesh/event/${eventId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: biomeshEventUpdate, signal
    },
      options);
    }



export const getUpdateEventBiomeshEventEventIdPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventBiomeshEventEventIdPut>>, TError,{eventId: string;data: BiomeshEventUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateEventBiomeshEventEventIdPut>>, TError,{eventId: string;data: BiomeshEventUpdate}, TContext> => {

const mutationKey = ['updateEventBiomeshEventEventIdPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateEventBiomeshEventEventIdPut>>, {eventId: string;data: BiomeshEventUpdate}> = (props) => {
          const {eventId,data} = props ?? {};

          return  updateEventBiomeshEventEventIdPut(eventId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateEventBiomeshEventEventIdPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateEventBiomeshEventEventIdPut>>>
    export type UpdateEventBiomeshEventEventIdPutMutationBody = BiomeshEventUpdate
    export type UpdateEventBiomeshEventEventIdPutMutationError = HTTPValidationError

    /**
 * @summary Update biomesh event
 */
export const useUpdateEventBiomeshEventEventIdPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateEventBiomeshEventEventIdPut>>, TError,{eventId: string;data: BiomeshEventUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateEventBiomeshEventEventIdPut>>,
        TError,
        {eventId: string;data: BiomeshEventUpdate},
        TContext
      > => {
      return useMutation(getUpdateEventBiomeshEventEventIdPutMutationOptions(options), queryClient);
    }

/**
 * @summary Patch biomesh event
 */
export const patchEventBiomeshEventEventIdPatch = (
    eventId: string,
    biomeshEventPatch: BiomeshEventPatch,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshEventResponse>(
      {url: `/biomesh/event/${eventId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: biomeshEventPatch, signal
    },
      options);
    }



export const getPatchEventBiomeshEventEventIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchEventBiomeshEventEventIdPatch>>, TError,{eventId: string;data: BiomeshEventPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchEventBiomeshEventEventIdPatch>>, TError,{eventId: string;data: BiomeshEventPatch}, TContext> => {

const mutationKey = ['patchEventBiomeshEventEventIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchEventBiomeshEventEventIdPatch>>, {eventId: string;data: BiomeshEventPatch}> = (props) => {
          const {eventId,data} = props ?? {};

          return  patchEventBiomeshEventEventIdPatch(eventId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchEventBiomeshEventEventIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchEventBiomeshEventEventIdPatch>>>
    export type PatchEventBiomeshEventEventIdPatchMutationBody = BiomeshEventPatch
    export type PatchEventBiomeshEventEventIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch biomesh event
 */
export const usePatchEventBiomeshEventEventIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchEventBiomeshEventEventIdPatch>>, TError,{eventId: string;data: BiomeshEventPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchEventBiomeshEventEventIdPatch>>,
        TError,
        {eventId: string;data: BiomeshEventPatch},
        TContext
      > => {
      return useMutation(getPatchEventBiomeshEventEventIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Delete biomesh event
 */
export const deleteEventBiomeshEventEventIdDelete = (
    eventId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/biomesh/event/${eventId}`, method: 'DELETE', signal
    },
      options);
    }



export const getDeleteEventBiomeshEventEventIdDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventBiomeshEventEventIdDelete>>, TError,{eventId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteEventBiomeshEventEventIdDelete>>, TError,{eventId: string}, TContext> => {

const mutationKey = ['deleteEventBiomeshEventEventIdDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteEventBiomeshEventEventIdDelete>>, {eventId: string}> = (props) => {
          const {eventId} = props ?? {};

          return  deleteEventBiomeshEventEventIdDelete(eventId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteEventBiomeshEventEventIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteEventBiomeshEventEventIdDelete>>>

    export type DeleteEventBiomeshEventEventIdDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete biomesh event
 */
export const useDeleteEventBiomeshEventEventIdDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteEventBiomeshEventEventIdDelete>>, TError,{eventId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteEventBiomeshEventEventIdDelete>>,
        TError,
        {eventId: string},
        TContext
      > => {
      return useMutation(getDeleteEventBiomeshEventEventIdDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary Create a new biomesh operation
 */
export const createOperationBiomeshOperationPost = (
    biomeshOperationCreate: BiomeshOperationCreate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshOperationResponse>(
      {url: `/biomesh/operation`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshOperationCreate, signal
    },
      options);
    }



export const getCreateOperationBiomeshOperationPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOperationBiomeshOperationPost>>, TError,{data: BiomeshOperationCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createOperationBiomeshOperationPost>>, TError,{data: BiomeshOperationCreate}, TContext> => {

const mutationKey = ['createOperationBiomeshOperationPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createOperationBiomeshOperationPost>>, {data: BiomeshOperationCreate}> = (props) => {
          const {data} = props ?? {};

          return  createOperationBiomeshOperationPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateOperationBiomeshOperationPostMutationResult = NonNullable<Awaited<ReturnType<typeof createOperationBiomeshOperationPost>>>
    export type CreateOperationBiomeshOperationPostMutationBody = BiomeshOperationCreate
    export type CreateOperationBiomeshOperationPostMutationError = HTTPValidationError

    /**
 * @summary Create a new biomesh operation
 */
export const useCreateOperationBiomeshOperationPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createOperationBiomeshOperationPost>>, TError,{data: BiomeshOperationCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createOperationBiomeshOperationPost>>,
        TError,
        {data: BiomeshOperationCreate},
        TContext
      > => {
      return useMutation(getCreateOperationBiomeshOperationPostMutationOptions(options), queryClient);
    }

/**
 * @summary Bulk upsert biomesh operations
 */
export const upsertOperationsBiomeshOperationUpsertPost = (
    biomeshOperationUpsertRequest: BiomeshOperationUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<number>(
      {url: `/biomesh/operation/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshOperationUpsertRequest, signal
    },
      options);
    }



export const getUpsertOperationsBiomeshOperationUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertOperationsBiomeshOperationUpsertPost>>, TError,{data: BiomeshOperationUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertOperationsBiomeshOperationUpsertPost>>, TError,{data: BiomeshOperationUpsertRequest}, TContext> => {

const mutationKey = ['upsertOperationsBiomeshOperationUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertOperationsBiomeshOperationUpsertPost>>, {data: BiomeshOperationUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertOperationsBiomeshOperationUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertOperationsBiomeshOperationUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertOperationsBiomeshOperationUpsertPost>>>
    export type UpsertOperationsBiomeshOperationUpsertPostMutationBody = BiomeshOperationUpsertRequest
    export type UpsertOperationsBiomeshOperationUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Bulk upsert biomesh operations
 */
export const useUpsertOperationsBiomeshOperationUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertOperationsBiomeshOperationUpsertPost>>, TError,{data: BiomeshOperationUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertOperationsBiomeshOperationUpsertPost>>,
        TError,
        {data: BiomeshOperationUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertOperationsBiomeshOperationUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List biomesh operations
 */
export const listOperationsBiomeshOperationListGet = (
    params?: ListOperationsBiomeshOperationListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshOperationResponse[]>(
      {url: `/biomesh/operation/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListOperationsBiomeshOperationListGetQueryKey = (params?: ListOperationsBiomeshOperationListGetParams,) => {
    return [
    `/biomesh/operation/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListOperationsBiomeshOperationListGetQueryOptions = <TData = Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError = HTTPValidationError>(params?: ListOperationsBiomeshOperationListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListOperationsBiomeshOperationListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>> = ({ signal }) => listOperationsBiomeshOperationListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListOperationsBiomeshOperationListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>>
export type ListOperationsBiomeshOperationListGetQueryError = HTTPValidationError


export function useListOperationsBiomeshOperationListGet<TData = Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListOperationsBiomeshOperationListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>,
          TError,
          Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOperationsBiomeshOperationListGet<TData = Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError = HTTPValidationError>(
 params?: ListOperationsBiomeshOperationListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>,
          TError,
          Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListOperationsBiomeshOperationListGet<TData = Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError = HTTPValidationError>(
 params?: ListOperationsBiomeshOperationListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List biomesh operations
 */

export function useListOperationsBiomeshOperationListGet<TData = Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError = HTTPValidationError>(
 params?: ListOperationsBiomeshOperationListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listOperationsBiomeshOperationListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListOperationsBiomeshOperationListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get biomesh operation by ID
 */
export const getOperationBiomeshOperationOperationIdGet = (
    operationId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshOperationResponse>(
      {url: `/biomesh/operation/${operationId}`, method: 'GET', signal
    },
      options);
    }




export const getGetOperationBiomeshOperationOperationIdGetQueryKey = (operationId: string,) => {
    return [
    `/biomesh/operation/${operationId}`
    ] as const;
    }


export const getGetOperationBiomeshOperationOperationIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError = HTTPValidationError>(operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetOperationBiomeshOperationOperationIdGetQueryKey(operationId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>> = ({ signal }) => getOperationBiomeshOperationOperationIdGet(operationId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: operationId !== null && operationId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetOperationBiomeshOperationOperationIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>>
export type GetOperationBiomeshOperationOperationIdGetQueryError = HTTPValidationError


export function useGetOperationBiomeshOperationOperationIdGet<TData = Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError = HTTPValidationError>(
 operationId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>,
          TError,
          Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOperationBiomeshOperationOperationIdGet<TData = Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError = HTTPValidationError>(
 operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>,
          TError,
          Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetOperationBiomeshOperationOperationIdGet<TData = Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError = HTTPValidationError>(
 operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get biomesh operation by ID
 */

export function useGetOperationBiomeshOperationOperationIdGet<TData = Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError = HTTPValidationError>(
 operationId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getOperationBiomeshOperationOperationIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetOperationBiomeshOperationOperationIdGetQueryOptions(operationId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Replace all scalar fields. Relationship ID lists use replace semantics: provide a list to set associations, [] to clear, or omit to leave unchanged.
 * @summary Update biomesh operation
 */
export const updateOperationBiomeshOperationOperationIdPut = (
    operationId: string,
    biomeshOperationUpdate: BiomeshOperationUpdate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshOperationResponse>(
      {url: `/biomesh/operation/${operationId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: biomeshOperationUpdate, signal
    },
      options);
    }



export const getUpdateOperationBiomeshOperationOperationIdPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOperationBiomeshOperationOperationIdPut>>, TError,{operationId: string;data: BiomeshOperationUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateOperationBiomeshOperationOperationIdPut>>, TError,{operationId: string;data: BiomeshOperationUpdate}, TContext> => {

const mutationKey = ['updateOperationBiomeshOperationOperationIdPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateOperationBiomeshOperationOperationIdPut>>, {operationId: string;data: BiomeshOperationUpdate}> = (props) => {
          const {operationId,data} = props ?? {};

          return  updateOperationBiomeshOperationOperationIdPut(operationId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateOperationBiomeshOperationOperationIdPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateOperationBiomeshOperationOperationIdPut>>>
    export type UpdateOperationBiomeshOperationOperationIdPutMutationBody = BiomeshOperationUpdate
    export type UpdateOperationBiomeshOperationOperationIdPutMutationError = HTTPValidationError

    /**
 * @summary Update biomesh operation
 */
export const useUpdateOperationBiomeshOperationOperationIdPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateOperationBiomeshOperationOperationIdPut>>, TError,{operationId: string;data: BiomeshOperationUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateOperationBiomeshOperationOperationIdPut>>,
        TError,
        {operationId: string;data: BiomeshOperationUpdate},
        TContext
      > => {
      return useMutation(getUpdateOperationBiomeshOperationOperationIdPutMutationOptions(options), queryClient);
    }

/**
 * @summary Patch biomesh operation
 */
export const patchOperationBiomeshOperationOperationIdPatch = (
    operationId: string,
    biomeshOperationPatch: BiomeshOperationPatch,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshOperationResponse>(
      {url: `/biomesh/operation/${operationId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: biomeshOperationPatch, signal
    },
      options);
    }



export const getPatchOperationBiomeshOperationOperationIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchOperationBiomeshOperationOperationIdPatch>>, TError,{operationId: string;data: BiomeshOperationPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchOperationBiomeshOperationOperationIdPatch>>, TError,{operationId: string;data: BiomeshOperationPatch}, TContext> => {

const mutationKey = ['patchOperationBiomeshOperationOperationIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchOperationBiomeshOperationOperationIdPatch>>, {operationId: string;data: BiomeshOperationPatch}> = (props) => {
          const {operationId,data} = props ?? {};

          return  patchOperationBiomeshOperationOperationIdPatch(operationId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchOperationBiomeshOperationOperationIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchOperationBiomeshOperationOperationIdPatch>>>
    export type PatchOperationBiomeshOperationOperationIdPatchMutationBody = BiomeshOperationPatch
    export type PatchOperationBiomeshOperationOperationIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch biomesh operation
 */
export const usePatchOperationBiomeshOperationOperationIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchOperationBiomeshOperationOperationIdPatch>>, TError,{operationId: string;data: BiomeshOperationPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchOperationBiomeshOperationOperationIdPatch>>,
        TError,
        {operationId: string;data: BiomeshOperationPatch},
        TContext
      > => {
      return useMutation(getPatchOperationBiomeshOperationOperationIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Delete biomesh operation
 */
export const deleteOperationBiomeshOperationOperationIdDelete = (
    operationId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/biomesh/operation/${operationId}`, method: 'DELETE', signal
    },
      options);
    }



export const getDeleteOperationBiomeshOperationOperationIdDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteOperationBiomeshOperationOperationIdDelete>>, TError,{operationId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteOperationBiomeshOperationOperationIdDelete>>, TError,{operationId: string}, TContext> => {

const mutationKey = ['deleteOperationBiomeshOperationOperationIdDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteOperationBiomeshOperationOperationIdDelete>>, {operationId: string}> = (props) => {
          const {operationId} = props ?? {};

          return  deleteOperationBiomeshOperationOperationIdDelete(operationId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteOperationBiomeshOperationOperationIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteOperationBiomeshOperationOperationIdDelete>>>

    export type DeleteOperationBiomeshOperationOperationIdDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete biomesh operation
 */
export const useDeleteOperationBiomeshOperationOperationIdDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteOperationBiomeshOperationOperationIdDelete>>, TError,{operationId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteOperationBiomeshOperationOperationIdDelete>>,
        TError,
        {operationId: string},
        TContext
      > => {
      return useMutation(getDeleteOperationBiomeshOperationOperationIdDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary Create a new biomesh platform
 */
export const createPlatformBiomeshPlatformPost = (
    biomeshPlatformCreate: BiomeshPlatformCreate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshPlatformResponse>(
      {url: `/biomesh/platform`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshPlatformCreate, signal
    },
      options);
    }



export const getCreatePlatformBiomeshPlatformPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformBiomeshPlatformPost>>, TError,{data: BiomeshPlatformCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createPlatformBiomeshPlatformPost>>, TError,{data: BiomeshPlatformCreate}, TContext> => {

const mutationKey = ['createPlatformBiomeshPlatformPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createPlatformBiomeshPlatformPost>>, {data: BiomeshPlatformCreate}> = (props) => {
          const {data} = props ?? {};

          return  createPlatformBiomeshPlatformPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreatePlatformBiomeshPlatformPostMutationResult = NonNullable<Awaited<ReturnType<typeof createPlatformBiomeshPlatformPost>>>
    export type CreatePlatformBiomeshPlatformPostMutationBody = BiomeshPlatformCreate
    export type CreatePlatformBiomeshPlatformPostMutationError = HTTPValidationError

    /**
 * @summary Create a new biomesh platform
 */
export const useCreatePlatformBiomeshPlatformPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createPlatformBiomeshPlatformPost>>, TError,{data: BiomeshPlatformCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createPlatformBiomeshPlatformPost>>,
        TError,
        {data: BiomeshPlatformCreate},
        TContext
      > => {
      return useMutation(getCreatePlatformBiomeshPlatformPostMutationOptions(options), queryClient);
    }

/**
 * @summary Bulk upsert biomesh platforms
 */
export const upsertPlatformsBiomeshPlatformUpsertPost = (
    biomeshPlatformUpsertRequest: BiomeshPlatformUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<number>(
      {url: `/biomesh/platform/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshPlatformUpsertRequest, signal
    },
      options);
    }



export const getUpsertPlatformsBiomeshPlatformUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertPlatformsBiomeshPlatformUpsertPost>>, TError,{data: BiomeshPlatformUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertPlatformsBiomeshPlatformUpsertPost>>, TError,{data: BiomeshPlatformUpsertRequest}, TContext> => {

const mutationKey = ['upsertPlatformsBiomeshPlatformUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertPlatformsBiomeshPlatformUpsertPost>>, {data: BiomeshPlatformUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertPlatformsBiomeshPlatformUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertPlatformsBiomeshPlatformUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertPlatformsBiomeshPlatformUpsertPost>>>
    export type UpsertPlatformsBiomeshPlatformUpsertPostMutationBody = BiomeshPlatformUpsertRequest
    export type UpsertPlatformsBiomeshPlatformUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Bulk upsert biomesh platforms
 */
export const useUpsertPlatformsBiomeshPlatformUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertPlatformsBiomeshPlatformUpsertPost>>, TError,{data: BiomeshPlatformUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertPlatformsBiomeshPlatformUpsertPost>>,
        TError,
        {data: BiomeshPlatformUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertPlatformsBiomeshPlatformUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List biomesh platforms
 */
export const listPlatformsBiomeshPlatformListGet = (
    params?: ListPlatformsBiomeshPlatformListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshPlatformResponse[]>(
      {url: `/biomesh/platform/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListPlatformsBiomeshPlatformListGetQueryKey = (params?: ListPlatformsBiomeshPlatformListGetParams,) => {
    return [
    `/biomesh/platform/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListPlatformsBiomeshPlatformListGetQueryOptions = <TData = Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError = HTTPValidationError>(params?: ListPlatformsBiomeshPlatformListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListPlatformsBiomeshPlatformListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>> = ({ signal }) => listPlatformsBiomeshPlatformListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListPlatformsBiomeshPlatformListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>>
export type ListPlatformsBiomeshPlatformListGetQueryError = HTTPValidationError


export function useListPlatformsBiomeshPlatformListGet<TData = Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListPlatformsBiomeshPlatformListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>,
          TError,
          Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPlatformsBiomeshPlatformListGet<TData = Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError = HTTPValidationError>(
 params?: ListPlatformsBiomeshPlatformListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>,
          TError,
          Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListPlatformsBiomeshPlatformListGet<TData = Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError = HTTPValidationError>(
 params?: ListPlatformsBiomeshPlatformListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List biomesh platforms
 */

export function useListPlatformsBiomeshPlatformListGet<TData = Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError = HTTPValidationError>(
 params?: ListPlatformsBiomeshPlatformListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listPlatformsBiomeshPlatformListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListPlatformsBiomeshPlatformListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get biomesh platform by ID
 */
export const getPlatformBiomeshPlatformPlatformIdGet = (
    platformId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshPlatformResponse>(
      {url: `/biomesh/platform/${platformId}`, method: 'GET', signal
    },
      options);
    }




export const getGetPlatformBiomeshPlatformPlatformIdGetQueryKey = (platformId: string,) => {
    return [
    `/biomesh/platform/${platformId}`
    ] as const;
    }


export const getGetPlatformBiomeshPlatformPlatformIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError = HTTPValidationError>(platformId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetPlatformBiomeshPlatformPlatformIdGetQueryKey(platformId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>> = ({ signal }) => getPlatformBiomeshPlatformPlatformIdGet(platformId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: platformId !== null && platformId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetPlatformBiomeshPlatformPlatformIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>>
export type GetPlatformBiomeshPlatformPlatformIdGetQueryError = HTTPValidationError


export function useGetPlatformBiomeshPlatformPlatformIdGet<TData = Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError = HTTPValidationError>(
 platformId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>,
          TError,
          Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPlatformBiomeshPlatformPlatformIdGet<TData = Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError = HTTPValidationError>(
 platformId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>,
          TError,
          Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetPlatformBiomeshPlatformPlatformIdGet<TData = Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError = HTTPValidationError>(
 platformId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get biomesh platform by ID
 */

export function useGetPlatformBiomeshPlatformPlatformIdGet<TData = Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError = HTTPValidationError>(
 platformId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getPlatformBiomeshPlatformPlatformIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetPlatformBiomeshPlatformPlatformIdGetQueryOptions(platformId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Replace all scalar fields. Relationship ID lists use replace semantics: provide a list to set associations, [] to clear, or omit to leave unchanged.
 * @summary Update biomesh platform
 */
export const updatePlatformBiomeshPlatformPlatformIdPut = (
    platformId: string,
    biomeshPlatformUpdate: BiomeshPlatformUpdate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshPlatformResponse>(
      {url: `/biomesh/platform/${platformId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: biomeshPlatformUpdate, signal
    },
      options);
    }



export const getUpdatePlatformBiomeshPlatformPlatformIdPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformBiomeshPlatformPlatformIdPut>>, TError,{platformId: string;data: BiomeshPlatformUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updatePlatformBiomeshPlatformPlatformIdPut>>, TError,{platformId: string;data: BiomeshPlatformUpdate}, TContext> => {

const mutationKey = ['updatePlatformBiomeshPlatformPlatformIdPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updatePlatformBiomeshPlatformPlatformIdPut>>, {platformId: string;data: BiomeshPlatformUpdate}> = (props) => {
          const {platformId,data} = props ?? {};

          return  updatePlatformBiomeshPlatformPlatformIdPut(platformId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdatePlatformBiomeshPlatformPlatformIdPutMutationResult = NonNullable<Awaited<ReturnType<typeof updatePlatformBiomeshPlatformPlatformIdPut>>>
    export type UpdatePlatformBiomeshPlatformPlatformIdPutMutationBody = BiomeshPlatformUpdate
    export type UpdatePlatformBiomeshPlatformPlatformIdPutMutationError = HTTPValidationError

    /**
 * @summary Update biomesh platform
 */
export const useUpdatePlatformBiomeshPlatformPlatformIdPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updatePlatformBiomeshPlatformPlatformIdPut>>, TError,{platformId: string;data: BiomeshPlatformUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updatePlatformBiomeshPlatformPlatformIdPut>>,
        TError,
        {platformId: string;data: BiomeshPlatformUpdate},
        TContext
      > => {
      return useMutation(getUpdatePlatformBiomeshPlatformPlatformIdPutMutationOptions(options), queryClient);
    }

/**
 * @summary Patch biomesh platform
 */
export const patchPlatformBiomeshPlatformPlatformIdPatch = (
    platformId: string,
    biomeshPlatformPatch: BiomeshPlatformPatch,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshPlatformResponse>(
      {url: `/biomesh/platform/${platformId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: biomeshPlatformPatch, signal
    },
      options);
    }



export const getPatchPlatformBiomeshPlatformPlatformIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchPlatformBiomeshPlatformPlatformIdPatch>>, TError,{platformId: string;data: BiomeshPlatformPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchPlatformBiomeshPlatformPlatformIdPatch>>, TError,{platformId: string;data: BiomeshPlatformPatch}, TContext> => {

const mutationKey = ['patchPlatformBiomeshPlatformPlatformIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchPlatformBiomeshPlatformPlatformIdPatch>>, {platformId: string;data: BiomeshPlatformPatch}> = (props) => {
          const {platformId,data} = props ?? {};

          return  patchPlatformBiomeshPlatformPlatformIdPatch(platformId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchPlatformBiomeshPlatformPlatformIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchPlatformBiomeshPlatformPlatformIdPatch>>>
    export type PatchPlatformBiomeshPlatformPlatformIdPatchMutationBody = BiomeshPlatformPatch
    export type PatchPlatformBiomeshPlatformPlatformIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch biomesh platform
 */
export const usePatchPlatformBiomeshPlatformPlatformIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchPlatformBiomeshPlatformPlatformIdPatch>>, TError,{platformId: string;data: BiomeshPlatformPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchPlatformBiomeshPlatformPlatformIdPatch>>,
        TError,
        {platformId: string;data: BiomeshPlatformPatch},
        TContext
      > => {
      return useMutation(getPatchPlatformBiomeshPlatformPlatformIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Delete biomesh platform
 */
export const deletePlatformBiomeshPlatformPlatformIdDelete = (
    platformId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/biomesh/platform/${platformId}`, method: 'DELETE', signal
    },
      options);
    }



export const getDeletePlatformBiomeshPlatformPlatformIdDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePlatformBiomeshPlatformPlatformIdDelete>>, TError,{platformId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deletePlatformBiomeshPlatformPlatformIdDelete>>, TError,{platformId: string}, TContext> => {

const mutationKey = ['deletePlatformBiomeshPlatformPlatformIdDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deletePlatformBiomeshPlatformPlatformIdDelete>>, {platformId: string}> = (props) => {
          const {platformId} = props ?? {};

          return  deletePlatformBiomeshPlatformPlatformIdDelete(platformId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeletePlatformBiomeshPlatformPlatformIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deletePlatformBiomeshPlatformPlatformIdDelete>>>

    export type DeletePlatformBiomeshPlatformPlatformIdDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete biomesh platform
 */
export const useDeletePlatformBiomeshPlatformPlatformIdDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deletePlatformBiomeshPlatformPlatformIdDelete>>, TError,{platformId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deletePlatformBiomeshPlatformPlatformIdDelete>>,
        TError,
        {platformId: string},
        TContext
      > => {
      return useMutation(getDeletePlatformBiomeshPlatformPlatformIdDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary Create a new biomesh sensor
 */
export const createSensorBiomeshSensorPost = (
    biomeshSensorCreate: BiomeshSensorCreate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshSensorResponse>(
      {url: `/biomesh/sensor`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshSensorCreate, signal
    },
      options);
    }



export const getCreateSensorBiomeshSensorPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSensorBiomeshSensorPost>>, TError,{data: BiomeshSensorCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createSensorBiomeshSensorPost>>, TError,{data: BiomeshSensorCreate}, TContext> => {

const mutationKey = ['createSensorBiomeshSensorPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createSensorBiomeshSensorPost>>, {data: BiomeshSensorCreate}> = (props) => {
          const {data} = props ?? {};

          return  createSensorBiomeshSensorPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateSensorBiomeshSensorPostMutationResult = NonNullable<Awaited<ReturnType<typeof createSensorBiomeshSensorPost>>>
    export type CreateSensorBiomeshSensorPostMutationBody = BiomeshSensorCreate
    export type CreateSensorBiomeshSensorPostMutationError = HTTPValidationError

    /**
 * @summary Create a new biomesh sensor
 */
export const useCreateSensorBiomeshSensorPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createSensorBiomeshSensorPost>>, TError,{data: BiomeshSensorCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createSensorBiomeshSensorPost>>,
        TError,
        {data: BiomeshSensorCreate},
        TContext
      > => {
      return useMutation(getCreateSensorBiomeshSensorPostMutationOptions(options), queryClient);
    }

/**
 * @summary Bulk upsert biomesh sensors
 */
export const upsertSensorsBiomeshSensorUpsertPost = (
    biomeshSensorUpsertRequest: BiomeshSensorUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<number>(
      {url: `/biomesh/sensor/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshSensorUpsertRequest, signal
    },
      options);
    }



export const getUpsertSensorsBiomeshSensorUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertSensorsBiomeshSensorUpsertPost>>, TError,{data: BiomeshSensorUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertSensorsBiomeshSensorUpsertPost>>, TError,{data: BiomeshSensorUpsertRequest}, TContext> => {

const mutationKey = ['upsertSensorsBiomeshSensorUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertSensorsBiomeshSensorUpsertPost>>, {data: BiomeshSensorUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertSensorsBiomeshSensorUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertSensorsBiomeshSensorUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertSensorsBiomeshSensorUpsertPost>>>
    export type UpsertSensorsBiomeshSensorUpsertPostMutationBody = BiomeshSensorUpsertRequest
    export type UpsertSensorsBiomeshSensorUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Bulk upsert biomesh sensors
 */
export const useUpsertSensorsBiomeshSensorUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertSensorsBiomeshSensorUpsertPost>>, TError,{data: BiomeshSensorUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertSensorsBiomeshSensorUpsertPost>>,
        TError,
        {data: BiomeshSensorUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertSensorsBiomeshSensorUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List biomesh sensors
 */
export const listSensorsBiomeshSensorListGet = (
    params?: ListSensorsBiomeshSensorListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshSensorResponse[]>(
      {url: `/biomesh/sensor/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListSensorsBiomeshSensorListGetQueryKey = (params?: ListSensorsBiomeshSensorListGetParams,) => {
    return [
    `/biomesh/sensor/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListSensorsBiomeshSensorListGetQueryOptions = <TData = Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError = HTTPValidationError>(params?: ListSensorsBiomeshSensorListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListSensorsBiomeshSensorListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>> = ({ signal }) => listSensorsBiomeshSensorListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListSensorsBiomeshSensorListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>>
export type ListSensorsBiomeshSensorListGetQueryError = HTTPValidationError


export function useListSensorsBiomeshSensorListGet<TData = Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListSensorsBiomeshSensorListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>,
          TError,
          Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSensorsBiomeshSensorListGet<TData = Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError = HTTPValidationError>(
 params?: ListSensorsBiomeshSensorListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>,
          TError,
          Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListSensorsBiomeshSensorListGet<TData = Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError = HTTPValidationError>(
 params?: ListSensorsBiomeshSensorListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List biomesh sensors
 */

export function useListSensorsBiomeshSensorListGet<TData = Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError = HTTPValidationError>(
 params?: ListSensorsBiomeshSensorListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listSensorsBiomeshSensorListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListSensorsBiomeshSensorListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get biomesh sensor by ID
 */
export const getSensorBiomeshSensorSensorIdGet = (
    sensorId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshSensorResponse>(
      {url: `/biomesh/sensor/${sensorId}`, method: 'GET', signal
    },
      options);
    }




export const getGetSensorBiomeshSensorSensorIdGetQueryKey = (sensorId: string,) => {
    return [
    `/biomesh/sensor/${sensorId}`
    ] as const;
    }


export const getGetSensorBiomeshSensorSensorIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError = HTTPValidationError>(sensorId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetSensorBiomeshSensorSensorIdGetQueryKey(sensorId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>> = ({ signal }) => getSensorBiomeshSensorSensorIdGet(sensorId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: sensorId !== null && sensorId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetSensorBiomeshSensorSensorIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>>
export type GetSensorBiomeshSensorSensorIdGetQueryError = HTTPValidationError


export function useGetSensorBiomeshSensorSensorIdGet<TData = Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError = HTTPValidationError>(
 sensorId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>,
          TError,
          Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSensorBiomeshSensorSensorIdGet<TData = Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError = HTTPValidationError>(
 sensorId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>,
          TError,
          Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetSensorBiomeshSensorSensorIdGet<TData = Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError = HTTPValidationError>(
 sensorId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get biomesh sensor by ID
 */

export function useGetSensorBiomeshSensorSensorIdGet<TData = Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError = HTTPValidationError>(
 sensorId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getSensorBiomeshSensorSensorIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetSensorBiomeshSensorSensorIdGetQueryOptions(sensorId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Replace all scalar fields. Relationship ID lists use replace semantics: provide a list to set associations, [] to clear, or omit to leave unchanged.
 * @summary Update biomesh sensor
 */
export const updateSensorBiomeshSensorSensorIdPut = (
    sensorId: string,
    biomeshSensorUpdate: BiomeshSensorUpdate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshSensorResponse>(
      {url: `/biomesh/sensor/${sensorId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: biomeshSensorUpdate, signal
    },
      options);
    }



export const getUpdateSensorBiomeshSensorSensorIdPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSensorBiomeshSensorSensorIdPut>>, TError,{sensorId: string;data: BiomeshSensorUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateSensorBiomeshSensorSensorIdPut>>, TError,{sensorId: string;data: BiomeshSensorUpdate}, TContext> => {

const mutationKey = ['updateSensorBiomeshSensorSensorIdPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateSensorBiomeshSensorSensorIdPut>>, {sensorId: string;data: BiomeshSensorUpdate}> = (props) => {
          const {sensorId,data} = props ?? {};

          return  updateSensorBiomeshSensorSensorIdPut(sensorId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateSensorBiomeshSensorSensorIdPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateSensorBiomeshSensorSensorIdPut>>>
    export type UpdateSensorBiomeshSensorSensorIdPutMutationBody = BiomeshSensorUpdate
    export type UpdateSensorBiomeshSensorSensorIdPutMutationError = HTTPValidationError

    /**
 * @summary Update biomesh sensor
 */
export const useUpdateSensorBiomeshSensorSensorIdPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateSensorBiomeshSensorSensorIdPut>>, TError,{sensorId: string;data: BiomeshSensorUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateSensorBiomeshSensorSensorIdPut>>,
        TError,
        {sensorId: string;data: BiomeshSensorUpdate},
        TContext
      > => {
      return useMutation(getUpdateSensorBiomeshSensorSensorIdPutMutationOptions(options), queryClient);
    }

/**
 * @summary Patch biomesh sensor
 */
export const patchSensorBiomeshSensorSensorIdPatch = (
    sensorId: string,
    biomeshSensorPatch: BiomeshSensorPatch,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshSensorResponse>(
      {url: `/biomesh/sensor/${sensorId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: biomeshSensorPatch, signal
    },
      options);
    }



export const getPatchSensorBiomeshSensorSensorIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchSensorBiomeshSensorSensorIdPatch>>, TError,{sensorId: string;data: BiomeshSensorPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchSensorBiomeshSensorSensorIdPatch>>, TError,{sensorId: string;data: BiomeshSensorPatch}, TContext> => {

const mutationKey = ['patchSensorBiomeshSensorSensorIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchSensorBiomeshSensorSensorIdPatch>>, {sensorId: string;data: BiomeshSensorPatch}> = (props) => {
          const {sensorId,data} = props ?? {};

          return  patchSensorBiomeshSensorSensorIdPatch(sensorId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchSensorBiomeshSensorSensorIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchSensorBiomeshSensorSensorIdPatch>>>
    export type PatchSensorBiomeshSensorSensorIdPatchMutationBody = BiomeshSensorPatch
    export type PatchSensorBiomeshSensorSensorIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch biomesh sensor
 */
export const usePatchSensorBiomeshSensorSensorIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchSensorBiomeshSensorSensorIdPatch>>, TError,{sensorId: string;data: BiomeshSensorPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchSensorBiomeshSensorSensorIdPatch>>,
        TError,
        {sensorId: string;data: BiomeshSensorPatch},
        TContext
      > => {
      return useMutation(getPatchSensorBiomeshSensorSensorIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Delete biomesh sensor
 */
export const deleteSensorBiomeshSensorSensorIdDelete = (
    sensorId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/biomesh/sensor/${sensorId}`, method: 'DELETE', signal
    },
      options);
    }



export const getDeleteSensorBiomeshSensorSensorIdDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSensorBiomeshSensorSensorIdDelete>>, TError,{sensorId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteSensorBiomeshSensorSensorIdDelete>>, TError,{sensorId: string}, TContext> => {

const mutationKey = ['deleteSensorBiomeshSensorSensorIdDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteSensorBiomeshSensorSensorIdDelete>>, {sensorId: string}> = (props) => {
          const {sensorId} = props ?? {};

          return  deleteSensorBiomeshSensorSensorIdDelete(sensorId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteSensorBiomeshSensorSensorIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteSensorBiomeshSensorSensorIdDelete>>>

    export type DeleteSensorBiomeshSensorSensorIdDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete biomesh sensor
 */
export const useDeleteSensorBiomeshSensorSensorIdDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteSensorBiomeshSensorSensorIdDelete>>, TError,{sensorId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteSensorBiomeshSensorSensorIdDelete>>,
        TError,
        {sensorId: string},
        TContext
      > => {
      return useMutation(getDeleteSensorBiomeshSensorSensorIdDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary Create a new biomesh unit
 */
export const createUnitBiomeshUnitPost = (
    biomeshUnitCreate: BiomeshUnitCreate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshUnitResponse>(
      {url: `/biomesh/unit`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshUnitCreate, signal
    },
      options);
    }



export const getCreateUnitBiomeshUnitPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createUnitBiomeshUnitPost>>, TError,{data: BiomeshUnitCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof createUnitBiomeshUnitPost>>, TError,{data: BiomeshUnitCreate}, TContext> => {

const mutationKey = ['createUnitBiomeshUnitPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof createUnitBiomeshUnitPost>>, {data: BiomeshUnitCreate}> = (props) => {
          const {data} = props ?? {};

          return  createUnitBiomeshUnitPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type CreateUnitBiomeshUnitPostMutationResult = NonNullable<Awaited<ReturnType<typeof createUnitBiomeshUnitPost>>>
    export type CreateUnitBiomeshUnitPostMutationBody = BiomeshUnitCreate
    export type CreateUnitBiomeshUnitPostMutationError = HTTPValidationError

    /**
 * @summary Create a new biomesh unit
 */
export const useCreateUnitBiomeshUnitPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof createUnitBiomeshUnitPost>>, TError,{data: BiomeshUnitCreate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof createUnitBiomeshUnitPost>>,
        TError,
        {data: BiomeshUnitCreate},
        TContext
      > => {
      return useMutation(getCreateUnitBiomeshUnitPostMutationOptions(options), queryClient);
    }

/**
 * @summary Bulk upsert biomesh units
 */
export const upsertUnitsBiomeshUnitUpsertPost = (
    biomeshUnitUpsertRequest: BiomeshUnitUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<number>(
      {url: `/biomesh/unit/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: biomeshUnitUpsertRequest, signal
    },
      options);
    }



export const getUpsertUnitsBiomeshUnitUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertUnitsBiomeshUnitUpsertPost>>, TError,{data: BiomeshUnitUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertUnitsBiomeshUnitUpsertPost>>, TError,{data: BiomeshUnitUpsertRequest}, TContext> => {

const mutationKey = ['upsertUnitsBiomeshUnitUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertUnitsBiomeshUnitUpsertPost>>, {data: BiomeshUnitUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertUnitsBiomeshUnitUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertUnitsBiomeshUnitUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertUnitsBiomeshUnitUpsertPost>>>
    export type UpsertUnitsBiomeshUnitUpsertPostMutationBody = BiomeshUnitUpsertRequest
    export type UpsertUnitsBiomeshUnitUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Bulk upsert biomesh units
 */
export const useUpsertUnitsBiomeshUnitUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertUnitsBiomeshUnitUpsertPost>>, TError,{data: BiomeshUnitUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertUnitsBiomeshUnitUpsertPost>>,
        TError,
        {data: BiomeshUnitUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertUnitsBiomeshUnitUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List biomesh units
 */
export const listUnitsBiomeshUnitListGet = (
    params?: ListUnitsBiomeshUnitListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshUnitResponse[]>(
      {url: `/biomesh/unit/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListUnitsBiomeshUnitListGetQueryKey = (params?: ListUnitsBiomeshUnitListGetParams,) => {
    return [
    `/biomesh/unit/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListUnitsBiomeshUnitListGetQueryOptions = <TData = Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError = HTTPValidationError>(params?: ListUnitsBiomeshUnitListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListUnitsBiomeshUnitListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>> = ({ signal }) => listUnitsBiomeshUnitListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListUnitsBiomeshUnitListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>>
export type ListUnitsBiomeshUnitListGetQueryError = HTTPValidationError


export function useListUnitsBiomeshUnitListGet<TData = Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListUnitsBiomeshUnitListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>,
          TError,
          Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListUnitsBiomeshUnitListGet<TData = Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError = HTTPValidationError>(
 params?: ListUnitsBiomeshUnitListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>,
          TError,
          Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListUnitsBiomeshUnitListGet<TData = Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError = HTTPValidationError>(
 params?: ListUnitsBiomeshUnitListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List biomesh units
 */

export function useListUnitsBiomeshUnitListGet<TData = Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError = HTTPValidationError>(
 params?: ListUnitsBiomeshUnitListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listUnitsBiomeshUnitListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListUnitsBiomeshUnitListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get biomesh unit by ID
 */
export const getUnitBiomeshUnitUnitIdGet = (
    unitId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshUnitResponse>(
      {url: `/biomesh/unit/${unitId}`, method: 'GET', signal
    },
      options);
    }




export const getGetUnitBiomeshUnitUnitIdGetQueryKey = (unitId: string,) => {
    return [
    `/biomesh/unit/${unitId}`
    ] as const;
    }


export const getGetUnitBiomeshUnitUnitIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError = HTTPValidationError>(unitId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetUnitBiomeshUnitUnitIdGetQueryKey(unitId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>> = ({ signal }) => getUnitBiomeshUnitUnitIdGet(unitId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: unitId !== null && unitId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetUnitBiomeshUnitUnitIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>>
export type GetUnitBiomeshUnitUnitIdGetQueryError = HTTPValidationError


export function useGetUnitBiomeshUnitUnitIdGet<TData = Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError = HTTPValidationError>(
 unitId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>,
          TError,
          Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUnitBiomeshUnitUnitIdGet<TData = Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError = HTTPValidationError>(
 unitId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>,
          TError,
          Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetUnitBiomeshUnitUnitIdGet<TData = Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError = HTTPValidationError>(
 unitId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get biomesh unit by ID
 */

export function useGetUnitBiomeshUnitUnitIdGet<TData = Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError = HTTPValidationError>(
 unitId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getUnitBiomeshUnitUnitIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetUnitBiomeshUnitUnitIdGetQueryOptions(unitId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * Replace all scalar fields. Relationship ID lists use replace semantics: provide a list to set associations, [] to clear, or omit to leave unchanged.
 * @summary Update biomesh unit
 */
export const updateUnitBiomeshUnitUnitIdPut = (
    unitId: string,
    biomeshUnitUpdate: BiomeshUnitUpdate,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshUnitResponse>(
      {url: `/biomesh/unit/${unitId}`, method: 'PUT',
      headers: {'Content-Type': 'application/json', },
      data: biomeshUnitUpdate, signal
    },
      options);
    }



export const getUpdateUnitBiomeshUnitUnitIdPutMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateUnitBiomeshUnitUnitIdPut>>, TError,{unitId: string;data: BiomeshUnitUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof updateUnitBiomeshUnitUnitIdPut>>, TError,{unitId: string;data: BiomeshUnitUpdate}, TContext> => {

const mutationKey = ['updateUnitBiomeshUnitUnitIdPut'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof updateUnitBiomeshUnitUnitIdPut>>, {unitId: string;data: BiomeshUnitUpdate}> = (props) => {
          const {unitId,data} = props ?? {};

          return  updateUnitBiomeshUnitUnitIdPut(unitId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpdateUnitBiomeshUnitUnitIdPutMutationResult = NonNullable<Awaited<ReturnType<typeof updateUnitBiomeshUnitUnitIdPut>>>
    export type UpdateUnitBiomeshUnitUnitIdPutMutationBody = BiomeshUnitUpdate
    export type UpdateUnitBiomeshUnitUnitIdPutMutationError = HTTPValidationError

    /**
 * @summary Update biomesh unit
 */
export const useUpdateUnitBiomeshUnitUnitIdPut = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof updateUnitBiomeshUnitUnitIdPut>>, TError,{unitId: string;data: BiomeshUnitUpdate}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof updateUnitBiomeshUnitUnitIdPut>>,
        TError,
        {unitId: string;data: BiomeshUnitUpdate},
        TContext
      > => {
      return useMutation(getUpdateUnitBiomeshUnitUnitIdPutMutationOptions(options), queryClient);
    }

/**
 * @summary Patch biomesh unit
 */
export const patchUnitBiomeshUnitUnitIdPatch = (
    unitId: string,
    biomeshUnitPatch: BiomeshUnitPatch,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiomeshUnitResponse>(
      {url: `/biomesh/unit/${unitId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: biomeshUnitPatch, signal
    },
      options);
    }



export const getPatchUnitBiomeshUnitUnitIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchUnitBiomeshUnitUnitIdPatch>>, TError,{unitId: string;data: BiomeshUnitPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchUnitBiomeshUnitUnitIdPatch>>, TError,{unitId: string;data: BiomeshUnitPatch}, TContext> => {

const mutationKey = ['patchUnitBiomeshUnitUnitIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchUnitBiomeshUnitUnitIdPatch>>, {unitId: string;data: BiomeshUnitPatch}> = (props) => {
          const {unitId,data} = props ?? {};

          return  patchUnitBiomeshUnitUnitIdPatch(unitId,data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchUnitBiomeshUnitUnitIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchUnitBiomeshUnitUnitIdPatch>>>
    export type PatchUnitBiomeshUnitUnitIdPatchMutationBody = BiomeshUnitPatch
    export type PatchUnitBiomeshUnitUnitIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch biomesh unit
 */
export const usePatchUnitBiomeshUnitUnitIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchUnitBiomeshUnitUnitIdPatch>>, TError,{unitId: string;data: BiomeshUnitPatch}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchUnitBiomeshUnitUnitIdPatch>>,
        TError,
        {unitId: string;data: BiomeshUnitPatch},
        TContext
      > => {
      return useMutation(getPatchUnitBiomeshUnitUnitIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Delete biomesh unit
 */
export const deleteUnitBiomeshUnitUnitIdDelete = (
    unitId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<void>(
      {url: `/biomesh/unit/${unitId}`, method: 'DELETE', signal
    },
      options);
    }



export const getDeleteUnitBiomeshUnitUnitIdDeleteMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteUnitBiomeshUnitUnitIdDelete>>, TError,{unitId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof deleteUnitBiomeshUnitUnitIdDelete>>, TError,{unitId: string}, TContext> => {

const mutationKey = ['deleteUnitBiomeshUnitUnitIdDelete'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof deleteUnitBiomeshUnitUnitIdDelete>>, {unitId: string}> = (props) => {
          const {unitId} = props ?? {};

          return  deleteUnitBiomeshUnitUnitIdDelete(unitId,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type DeleteUnitBiomeshUnitUnitIdDeleteMutationResult = NonNullable<Awaited<ReturnType<typeof deleteUnitBiomeshUnitUnitIdDelete>>>

    export type DeleteUnitBiomeshUnitUnitIdDeleteMutationError = HTTPValidationError

    /**
 * @summary Delete biomesh unit
 */
export const useDeleteUnitBiomeshUnitUnitIdDelete = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof deleteUnitBiomeshUnitUnitIdDelete>>, TError,{unitId: string}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof deleteUnitBiomeshUnitUnitIdDelete>>,
        TError,
        {unitId: string},
        TContext
      > => {
      return useMutation(getDeleteUnitBiomeshUnitUnitIdDeleteMutationOptions(options), queryClient);
    }

/**
 * @summary Create or update GDELT articles
 */
export const upsertGdeltArticlesGdeltArticlesUpsertPost = (
    gdeltArticleUpsertRequest: GdeltArticleUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltArticleUpsertResponse>(
      {url: `/gdelt/articles/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: gdeltArticleUpsertRequest, signal
    },
      options);
    }



export const getUpsertGdeltArticlesGdeltArticlesUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltArticlesGdeltArticlesUpsertPost>>, TError,{data: GdeltArticleUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltArticlesGdeltArticlesUpsertPost>>, TError,{data: GdeltArticleUpsertRequest}, TContext> => {

const mutationKey = ['upsertGdeltArticlesGdeltArticlesUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertGdeltArticlesGdeltArticlesUpsertPost>>, {data: GdeltArticleUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertGdeltArticlesGdeltArticlesUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertGdeltArticlesGdeltArticlesUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertGdeltArticlesGdeltArticlesUpsertPost>>>
    export type UpsertGdeltArticlesGdeltArticlesUpsertPostMutationBody = GdeltArticleUpsertRequest
    export type UpsertGdeltArticlesGdeltArticlesUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update GDELT articles
 */
export const useUpsertGdeltArticlesGdeltArticlesUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltArticlesGdeltArticlesUpsertPost>>, TError,{data: GdeltArticleUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertGdeltArticlesGdeltArticlesUpsertPost>>,
        TError,
        {data: GdeltArticleUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertGdeltArticlesGdeltArticlesUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary List GDELT articles
 */
export const listGdeltArticlesGdeltArticlesListGet = (
    params?: ListGdeltArticlesGdeltArticlesListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltListResponseGdeltArticle>(
      {url: `/gdelt/articles/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListGdeltArticlesGdeltArticlesListGetQueryKey = (params?: ListGdeltArticlesGdeltArticlesListGetParams,) => {
    return [
    `/gdelt/articles/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGdeltArticlesGdeltArticlesListGetQueryOptions = <TData = Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError = HTTPValidationError>(params?: ListGdeltArticlesGdeltArticlesListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGdeltArticlesGdeltArticlesListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>> = ({ signal }) => listGdeltArticlesGdeltArticlesListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListGdeltArticlesGdeltArticlesListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>>
export type ListGdeltArticlesGdeltArticlesListGetQueryError = HTTPValidationError


export function useListGdeltArticlesGdeltArticlesListGet<TData = Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListGdeltArticlesGdeltArticlesListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltArticlesGdeltArticlesListGet<TData = Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError = HTTPValidationError>(
 params?: ListGdeltArticlesGdeltArticlesListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltArticlesGdeltArticlesListGet<TData = Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError = HTTPValidationError>(
 params?: ListGdeltArticlesGdeltArticlesListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List GDELT articles
 */

export function useListGdeltArticlesGdeltArticlesListGet<TData = Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError = HTTPValidationError>(
 params?: ListGdeltArticlesGdeltArticlesListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltArticlesGdeltArticlesListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListGdeltArticlesGdeltArticlesListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Create or update GDELT signal clusters
 */
export const upsertGdeltSignalClustersGdeltSignalClustersUpsertPost = (
    gdeltSignalClusterUpsertRequest: GdeltSignalClusterUpsertRequest,
    params?: UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltSignalClusterUpsertResponse>(
      {url: `/gdelt/signal-clusters/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: gdeltSignalClusterUpsertRequest,
        params, signal
    },
      options);
    }



export const getUpsertGdeltSignalClustersGdeltSignalClustersUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltSignalClustersGdeltSignalClustersUpsertPost>>, TError,{data: GdeltSignalClusterUpsertRequest;params?: UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltSignalClustersGdeltSignalClustersUpsertPost>>, TError,{data: GdeltSignalClusterUpsertRequest;params?: UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams}, TContext> => {

const mutationKey = ['upsertGdeltSignalClustersGdeltSignalClustersUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertGdeltSignalClustersGdeltSignalClustersUpsertPost>>, {data: GdeltSignalClusterUpsertRequest;params?: UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams}> = (props) => {
          const {data,params} = props ?? {};

          return  upsertGdeltSignalClustersGdeltSignalClustersUpsertPost(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertGdeltSignalClustersGdeltSignalClustersUpsertPost>>>
    export type UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostMutationBody = GdeltSignalClusterUpsertRequest
    export type UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Create or update GDELT signal clusters
 */
export const useUpsertGdeltSignalClustersGdeltSignalClustersUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltSignalClustersGdeltSignalClustersUpsertPost>>, TError,{data: GdeltSignalClusterUpsertRequest;params?: UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertGdeltSignalClustersGdeltSignalClustersUpsertPost>>,
        TError,
        {data: GdeltSignalClusterUpsertRequest;params?: UpsertGdeltSignalClustersGdeltSignalClustersUpsertPostParams},
        TContext
      > => {
      return useMutation(getUpsertGdeltSignalClustersGdeltSignalClustersUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary Delete clusters (and their members) whose window started on or after a given date
 */
export const truncateGdeltSignalClustersGdeltSignalClustersTruncatePost = (
    params?: TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltTruncateClustersResponse>(
      {url: `/gdelt/signal-clusters/truncate`, method: 'POST',
        params, signal
    },
      options);
    }



export const getTruncateGdeltSignalClustersGdeltSignalClustersTruncatePostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof truncateGdeltSignalClustersGdeltSignalClustersTruncatePost>>, TError,{params?: TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof truncateGdeltSignalClustersGdeltSignalClustersTruncatePost>>, TError,{params?: TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams}, TContext> => {

const mutationKey = ['truncateGdeltSignalClustersGdeltSignalClustersTruncatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof truncateGdeltSignalClustersGdeltSignalClustersTruncatePost>>, {params?: TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams}> = (props) => {
          const {params} = props ?? {};

          return  truncateGdeltSignalClustersGdeltSignalClustersTruncatePost(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostMutationResult = NonNullable<Awaited<ReturnType<typeof truncateGdeltSignalClustersGdeltSignalClustersTruncatePost>>>

    export type TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostMutationError = HTTPValidationError

    /**
 * @summary Delete clusters (and their members) whose window started on or after a given date
 */
export const useTruncateGdeltSignalClustersGdeltSignalClustersTruncatePost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof truncateGdeltSignalClustersGdeltSignalClustersTruncatePost>>, TError,{params?: TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof truncateGdeltSignalClustersGdeltSignalClustersTruncatePost>>,
        TError,
        {params?: TruncateGdeltSignalClustersGdeltSignalClustersTruncatePostParams},
        TContext
      > => {
      return useMutation(getTruncateGdeltSignalClustersGdeltSignalClustersTruncatePostMutationOptions(options), queryClient);
    }

/**
 * @summary List GDELT signal clusters
 */
export const listGdeltSignalClustersGdeltSignalClustersListGet = (
    params?: ListGdeltSignalClustersGdeltSignalClustersListGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltListResponseGdeltSignalCluster>(
      {url: `/gdelt/signal-clusters/list`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListGdeltSignalClustersGdeltSignalClustersListGetQueryKey = (params?: ListGdeltSignalClustersGdeltSignalClustersListGetParams,) => {
    return [
    `/gdelt/signal-clusters/list`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGdeltSignalClustersGdeltSignalClustersListGetQueryOptions = <TData = Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError = HTTPValidationError>(params?: ListGdeltSignalClustersGdeltSignalClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGdeltSignalClustersGdeltSignalClustersListGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>> = ({ signal }) => listGdeltSignalClustersGdeltSignalClustersListGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListGdeltSignalClustersGdeltSignalClustersListGetQueryResult = NonNullable<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>>
export type ListGdeltSignalClustersGdeltSignalClustersListGetQueryError = HTTPValidationError


export function useListGdeltSignalClustersGdeltSignalClustersListGet<TData = Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError = HTTPValidationError>(
 params: undefined |  ListGdeltSignalClustersGdeltSignalClustersListGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltSignalClustersGdeltSignalClustersListGet<TData = Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError = HTTPValidationError>(
 params?: ListGdeltSignalClustersGdeltSignalClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltSignalClustersGdeltSignalClustersListGet<TData = Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError = HTTPValidationError>(
 params?: ListGdeltSignalClustersGdeltSignalClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List GDELT signal clusters
 */

export function useListGdeltSignalClustersGdeltSignalClustersListGet<TData = Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError = HTTPValidationError>(
 params?: ListGdeltSignalClustersGdeltSignalClustersListGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClustersGdeltSignalClustersListGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListGdeltSignalClustersGdeltSignalClustersListGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary List all GDELT articles assigned to a cluster
 */
export const listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet = (
    clusterId: string,
    params?: ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltClusterMemberArticleList>(
      {url: `/gdelt/signal-clusters/${clusterId}/members`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetQueryKey = (clusterId: string,
    params?: ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams,) => {
    return [
    `/gdelt/signal-clusters/${clusterId}/members`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetQueryOptions = <TData = Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError = HTTPValidationError>(clusterId: string,
    params?: ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetQueryKey(clusterId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>> = ({ signal }) => listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet(clusterId,params, requestOptions, signal);





   return  { queryKey, queryFn, enabled: clusterId !== null && clusterId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetQueryResult = NonNullable<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>>
export type ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetQueryError = HTTPValidationError


export function useListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet<TData = Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params: undefined |  ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet<TData = Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params?: ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet<TData = Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params?: ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List all GDELT articles assigned to a cluster
 */

export function useListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet<TData = Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params?: ListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListGdeltSignalClusterMembersGdeltSignalClustersClusterIdMembersGetQueryOptions(clusterId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get a GDELT signal cluster by id
 */
export const getGdeltSignalClusterGdeltSignalClustersClusterIdGet = (
    clusterId: string,
    params?: GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltSignalCluster>(
      {url: `/gdelt/signal-clusters/${clusterId}`, method: 'GET',
        params, signal
    },
      options);
    }




export const getGetGdeltSignalClusterGdeltSignalClustersClusterIdGetQueryKey = (clusterId: string,
    params?: GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams,) => {
    return [
    `/gdelt/signal-clusters/${clusterId}`, ...(params ? [params] : [])
    ] as const;
    }


export const getGetGdeltSignalClusterGdeltSignalClustersClusterIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError = HTTPValidationError>(clusterId: string,
    params?: GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGdeltSignalClusterGdeltSignalClustersClusterIdGetQueryKey(clusterId,params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>> = ({ signal }) => getGdeltSignalClusterGdeltSignalClustersClusterIdGet(clusterId,params, requestOptions, signal);





   return  { queryKey, queryFn, enabled: clusterId !== null && clusterId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetGdeltSignalClusterGdeltSignalClustersClusterIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>>
export type GetGdeltSignalClusterGdeltSignalClustersClusterIdGetQueryError = HTTPValidationError


export function useGetGdeltSignalClusterGdeltSignalClustersClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params: undefined |  GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>,
          TError,
          Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetGdeltSignalClusterGdeltSignalClustersClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params?: GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>,
          TError,
          Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetGdeltSignalClusterGdeltSignalClustersClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params?: GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get a GDELT signal cluster by id
 */

export function useGetGdeltSignalClusterGdeltSignalClustersClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string,
    params?: GetGdeltSignalClusterGdeltSignalClustersClusterIdGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltSignalClusterGdeltSignalClustersClusterIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetGdeltSignalClusterGdeltSignalClustersClusterIdGetQueryOptions(clusterId,params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Patch GDELT signal cluster fields
 */
export const patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch = (
    clusterId: string,
    gdeltSignalClusterPatch: GdeltSignalClusterPatch,
    params?: PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltSignalCluster>(
      {url: `/gdelt/signal-clusters/${clusterId}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: gdeltSignalClusterPatch,
        params, signal
    },
      options);
    }



export const getPatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch>>, TError,{clusterId: string;data: GdeltSignalClusterPatch;params?: PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch>>, TError,{clusterId: string;data: GdeltSignalClusterPatch;params?: PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams}, TContext> => {

const mutationKey = ['patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch>>, {clusterId: string;data: GdeltSignalClusterPatch;params?: PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams}> = (props) => {
          const {clusterId,data,params} = props ?? {};

          return  patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch(clusterId,data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchMutationResult = NonNullable<Awaited<ReturnType<typeof patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch>>>
    export type PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchMutationBody = GdeltSignalClusterPatch
    export type PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchMutationError = HTTPValidationError

    /**
 * @summary Patch GDELT signal cluster fields
 */
export const usePatchGdeltSignalClusterGdeltSignalClustersClusterIdPatch = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch>>, TError,{clusterId: string;data: GdeltSignalClusterPatch;params?: PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof patchGdeltSignalClusterGdeltSignalClustersClusterIdPatch>>,
        TError,
        {clusterId: string;data: GdeltSignalClusterPatch;params?: PatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchParams},
        TContext
      > => {
      return useMutation(getPatchGdeltSignalClusterGdeltSignalClustersClusterIdPatchMutationOptions(options), queryClient);
    }

/**
 * @summary Find closest active cluster for assignment
 */
export const searchGdeltSignalClustersGdeltSignalClustersSearchPost = (
    clusterSearchRequest: ClusterSearchRequest,
    params?: SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltSignalCluster | null | void>(
      {url: `/gdelt/signal-clusters/search`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: clusterSearchRequest,
        params, signal
    },
      options);
    }



export const getSearchGdeltSignalClustersGdeltSignalClustersSearchPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof searchGdeltSignalClustersGdeltSignalClustersSearchPost>>, TError,{data: ClusterSearchRequest;params?: SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof searchGdeltSignalClustersGdeltSignalClustersSearchPost>>, TError,{data: ClusterSearchRequest;params?: SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams}, TContext> => {

const mutationKey = ['searchGdeltSignalClustersGdeltSignalClustersSearchPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof searchGdeltSignalClustersGdeltSignalClustersSearchPost>>, {data: ClusterSearchRequest;params?: SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams}> = (props) => {
          const {data,params} = props ?? {};

          return  searchGdeltSignalClustersGdeltSignalClustersSearchPost(data,params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type SearchGdeltSignalClustersGdeltSignalClustersSearchPostMutationResult = NonNullable<Awaited<ReturnType<typeof searchGdeltSignalClustersGdeltSignalClustersSearchPost>>>
    export type SearchGdeltSignalClustersGdeltSignalClustersSearchPostMutationBody = ClusterSearchRequest
    export type SearchGdeltSignalClustersGdeltSignalClustersSearchPostMutationError = HTTPValidationError

    /**
 * @summary Find closest active cluster for assignment
 */
export const useSearchGdeltSignalClustersGdeltSignalClustersSearchPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof searchGdeltSignalClustersGdeltSignalClustersSearchPost>>, TError,{data: ClusterSearchRequest;params?: SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof searchGdeltSignalClustersGdeltSignalClustersSearchPost>>,
        TError,
        {data: ClusterSearchRequest;params?: SearchGdeltSignalClustersGdeltSignalClustersSearchPostParams},
        TContext
      > => {
      return useMutation(getSearchGdeltSignalClustersGdeltSignalClustersSearchPostMutationOptions(options), queryClient);
    }

/**
 * @summary Append cluster members (idempotent)
 */
export const upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost = (
    gdeltSignalClusterMemberUpsertRequest: GdeltSignalClusterMemberUpsertRequest,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltSignalClusterMemberUpsertResponse>(
      {url: `/gdelt/signal-cluster-members/upsert`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: gdeltSignalClusterMemberUpsertRequest, signal
    },
      options);
    }



export const getUpsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost>>, TError,{data: GdeltSignalClusterMemberUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost>>, TError,{data: GdeltSignalClusterMemberUpsertRequest}, TContext> => {

const mutationKey = ['upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost>>, {data: GdeltSignalClusterMemberUpsertRequest}> = (props) => {
          const {data} = props ?? {};

          return  upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost(data,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type UpsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPostMutationResult = NonNullable<Awaited<ReturnType<typeof upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost>>>
    export type UpsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPostMutationBody = GdeltSignalClusterMemberUpsertRequest
    export type UpsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPostMutationError = HTTPValidationError

    /**
 * @summary Append cluster members (idempotent)
 */
export const useUpsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost>>, TError,{data: GdeltSignalClusterMemberUpsertRequest}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof upsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPost>>,
        TError,
        {data: GdeltSignalClusterMemberUpsertRequest},
        TContext
      > => {
      return useMutation(getUpsertGdeltSignalClusterMembersGdeltSignalClusterMembersUpsertPostMutationOptions(options), queryClient);
    }

/**
 * @summary Delete cluster members, optionally filtered by article publish date
 */
export const truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost = (
    params?: TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltTruncateMembersResponse>(
      {url: `/gdelt/signal-cluster-members/truncate`, method: 'POST',
        params, signal
    },
      options);
    }



export const getTruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostMutationOptions = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost>>, TError,{params?: TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
): UseMutationOptions<Awaited<ReturnType<typeof truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost>>, TError,{params?: TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams}, TContext> => {

const mutationKey = ['truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost'];
const {mutation: mutationOptions, request: requestOptions} = options ?
      options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey ?
      options
      : {...options, mutation: {...options.mutation, mutationKey}}
      : {mutation: { mutationKey, }, request: undefined};




      const mutationFn: MutationFunction<Awaited<ReturnType<typeof truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost>>, {params?: TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams}> = (props) => {
          const {params} = props ?? {};

          return  truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost(params,requestOptions)
        }






  return  { mutationFn, ...mutationOptions }}

    export type TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostMutationResult = NonNullable<Awaited<ReturnType<typeof truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost>>>

    export type TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostMutationError = HTTPValidationError

    /**
 * @summary Delete cluster members, optionally filtered by article publish date
 */
export const useTruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost = <TError = HTTPValidationError,
    TContext = unknown>(options?: { mutation?:UseMutationOptions<Awaited<ReturnType<typeof truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost>>, TError,{params?: TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams}, TContext>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient): UseMutationResult<
        Awaited<ReturnType<typeof truncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePost>>,
        TError,
        {params?: TruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostParams},
        TContext
      > => {
      return useMutation(getTruncateGdeltSignalClusterMembersGdeltSignalClusterMembersTruncatePostMutationOptions(options), queryClient);
    }

/**
 * @summary Distinct GDELT filter options for a date window
 */
export const listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet = (
    params?: ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<GdeltBiothreatSignalsFacets>(
      {url: `/gdelt/biothreat-signals/facets`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetQueryKey = (params?: ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams,) => {
    return [
    `/gdelt/biothreat-signals/facets`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetQueryOptions = <TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(params?: ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>> = ({ signal }) => listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetQueryResult = NonNullable<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>>
export type ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetQueryError = HTTPValidationError


export function useListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params: undefined |  ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params?: ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params?: ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Distinct GDELT filter options for a date window
 */

export function useListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError = HTTPValidationError>(
 params?: ListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListGdeltBiothreatSignalsFacetsGdeltBiothreatSignalsFacetsGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary List precomputed GDELT biothreat signals
 */
export const listGdeltBiothreatSignalsGdeltBiothreatSignalsGet = (
    params?: ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiothreatSignalList>(
      {url: `/gdelt/biothreat-signals`, method: 'GET',
        params, signal
    },
      options);
    }




export const getListGdeltBiothreatSignalsGdeltBiothreatSignalsGetQueryKey = (params?: ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams,) => {
    return [
    `/gdelt/biothreat-signals`, ...(params ? [params] : [])
    ] as const;
    }


export const getListGdeltBiothreatSignalsGdeltBiothreatSignalsGetQueryOptions = <TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError = HTTPValidationError>(params?: ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getListGdeltBiothreatSignalsGdeltBiothreatSignalsGetQueryKey(params);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>> = ({ signal }) => listGdeltBiothreatSignalsGdeltBiothreatSignalsGet(params, requestOptions, signal);





   return  { queryKey, queryFn, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetQueryResult = NonNullable<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>>
export type ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetQueryError = HTTPValidationError


export function useListGdeltBiothreatSignalsGdeltBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params: undefined |  ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltBiothreatSignalsGdeltBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params?: ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>,
          TError,
          Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useListGdeltBiothreatSignalsGdeltBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params?: ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary List precomputed GDELT biothreat signals
 */

export function useListGdeltBiothreatSignalsGdeltBiothreatSignalsGet<TData = Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError = HTTPValidationError>(
 params?: ListGdeltBiothreatSignalsGdeltBiothreatSignalsGetParams, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof listGdeltBiothreatSignalsGdeltBiothreatSignalsGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getListGdeltBiothreatSignalsGdeltBiothreatSignalsGetQueryOptions(params,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}







/**
 * @summary Get one precomputed GDELT biothreat signal by cluster id
 */
export const getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet = (
    clusterId: string,
 options?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>,signal?: AbortSignal
) => {


      return threatAnalyticsAxiosGenInstance<BiothreatSignal>(
      {url: `/gdelt/biothreat-signals/${clusterId}`, method: 'GET', signal
    },
      options);
    }




export const getGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGetQueryKey = (clusterId: string,) => {
    return [
    `/gdelt/biothreat-signals/${clusterId}`
    ] as const;
    }


export const getGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGetQueryOptions = <TData = Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError = HTTPValidationError>(clusterId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
) => {

const {query: queryOptions, request: requestOptions} = options ?? {};

  const queryKey =  queryOptions?.queryKey ?? getGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGetQueryKey(clusterId);



    const queryFn: QueryFunction<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>> = ({ signal }) => getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet(clusterId, requestOptions, signal);





   return  { queryKey, queryFn, enabled: clusterId !== null && clusterId !== undefined, ...queryOptions} as UseQueryOptions<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError, TData> & { queryKey: DataTag<QueryKey, TData, TError> }
}

export type GetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGetQueryResult = NonNullable<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>>
export type GetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGetQueryError = HTTPValidationError


export function useGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string, options: { query:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError, TData>> & Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>,
          TError,
          Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError, TData>> & Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>,
          TError,
          Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>
        > , 'initialData'
      >, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
export function useGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
  ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> }
/**
 * @summary Get one precomputed GDELT biothreat signal by cluster id
 */

export function useGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet<TData = Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError = HTTPValidationError>(
 clusterId: string, options?: { query?:Partial<UseQueryOptions<Awaited<ReturnType<typeof getGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGet>>, TError, TData>>, request?: SecondParameter<typeof threatAnalyticsAxiosGenInstance>}
 , queryClient?: QueryClient
 ):  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {

  const queryOptions = getGetGdeltBiothreatSignalGdeltBiothreatSignalsClusterIdGetQueryOptions(clusterId,options)

  const query = useQuery(queryOptions, queryClient) as  UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

  return { ...query, queryKey: queryOptions.queryKey };
}
